import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  Chip,
  Divider,
  Avatar,
  IconButton,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress,
  Collapse,
  Checkbox,
  Menu,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  useTheme,
  useMediaQuery,
  ToggleButton,
  ToggleButtonGroup,
  Radio,
  RadioGroup,
  FormControlLabel,
} from '@mui/material';
import {
  Close as CloseIcon,
  Phone as PhoneIcon,
  CalendarMonth as CalendarIcon,
  Link as LinkIcon,
  ContentCopy as CopyIcon,
  CheckCircle as CheckCircleIcon,
  Person as PersonIcon,
  AutoAwesome as AIIcon,
  Send as SendIcon,
  AttachFile as AttachIcon,
  PersonAdd as AssignIcon,
  Description as DetailsIcon,
  InfoOutlined as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
  Add as ExpandIcon,
  Remove as CollapseIcon,
  Event as FollowUpIcon,
  DeleteOutline as DeleteIcon,
  HistoryToggleOff as HistoryIcon,
  Save as SaveIcon,
  SwapHoriz as SwapIcon,
  OpenInNew as OpenInNewIcon,
  AccessTime as TimeIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { Popover } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ConfirmDialog from '../common/ConfirmDialog';
import { nextBookableDate } from './nextBookableDate';
import { canReplaceDraft, draftForBox } from './schedulingDraftSync';
import { overflowActions } from './taskQuickActions';
import { shouldPoll, describeTaskChange, TASK_POLL_MS } from './taskLivePoll';
import { suggestServiceMapping } from './suggestServiceMapping';
import { sendResponseHint } from './schedulingPanelCopy';
import { planSend } from './sendPath';
import { offerSeedAction } from './offerSeed';
import { slotsDraftMessage } from './slotsDraftMessage';
import { withInvite } from './bookingInvite';
import { bookingConfirmation } from './bookingConfirmation';
import { heldOfferMessage, linkLifetimePhrase, holdDays } from './heldOfferMessage';
import { linkRequest, previewGroups, holdLinkHint } from './offerAppointments';
import { typedTimeRefusal, typedSlot, alreadyOffered, inTimeOrder } from './typedTime';
import { offerSteps, nextProposalFrom, bundleWarnings, pickingForStep, BundleRule, OfferStep } from './offerSteps';
import { windowWarnings } from './offerWindow';
import { requestedWindowFromThread } from './threadWindow';
import { statusChoices, TaskStatusKey } from './taskStatusControl';
import { bookedReplyText, BookedForReply } from './bookedReply';
import { lapsedLinkNote } from './lapsedLink';
import { formatClock, clockFromParts, HOUR_CHOICES, MINUTE_CHOICES } from '../Calendar/appointmentClock';
import { heldUntilLabel } from './offerMessage';
import { renderOutreach, STANDARD_OUTREACH_TEMPLATE } from './wellnessOutreach';
import { describeLinkDrift } from './linkOfferDrift';
import { linkSentAt } from './linkSentAt';
import { describeLinkSend } from './linkSendState';
import { cancellationBannerCopy } from './cancellationBanner';
import { describeHoldImpact, HoldImpact } from './holdImpact';
import { cancellationLead } from './cancellationDraft';
import { taskNextStep } from './taskNextStep';
import { statusChip, priorityChip, headerSubtitle } from './taskHeaderFacts';
import { turnChip } from './turnChip';
import { patientDayLabel, patientTimeLabel } from '../../pages/publicBookingTime';
import { confirmationSentAt } from './confirmationSent';
import { practiceDateTimeLabel, getPracticeTimeZone } from '../../utils/practiceTime';
import { slotDayHeading } from './slotDayHeading';
import { Task, TaskCategory, Appointment, MessageAttachment, Contact, UnfiledLabFax, InternalNote } from '../../types';
import { triageApi, contactsApi, unfiledLabsApi, aiSchedulingApi, schedulingApi, appointmentsApi, serviceMappingApi, settingsApi, schedulingLinksApi, aiSchedulingConfigApi } from '../../services/api';
import AppointmentModal from '../Calendar/AppointmentModal';
import PatientLinkPicker, { SearchPatient } from './PatientLinkPicker';
import { useAuthStore } from '../../store/authStore';
import { formatDateTime, formatDateOnly, formatTimeOnly, formatClockString } from '../../utils/dateTime';

interface TaskDetailsModalProps {
  open: boolean;
  task: Task | null;
  onClose: () => void;
  onUpdate: (task: Task) => void;
  /**
   * A reply to open with, handed over from elsewhere — cancelling an
   * appointment writes the note to the patient here rather than sending it.
   */
  initialResponse?: string;
  /** A recall task was deleted — it has to leave the board, not be replaced. */
  onDeleted?: (taskId: string) => void;
  /** Open the patient's chart in place (drawer over this modal) instead of
   *  navigating away. Provided by TriageBoard, which hosts the chart surface. */
  onOpenChart?: (target: { spruceEntityId: string; name?: string; phone?: string; email?: string }) => void;
}

type ServiceProviderMapping = {
  id: string;
  service: { id: string; name: string; duration: number };
  specialist: { id: string; name: string; workingHours?: Record<string, any[]> };
};

type SchedulingSlot = {
  id?: string;
  date?: string;
  time?: string;
  startTime?: string;
  timeDisplay?: string;
  specialist?: string;
  specialistId?: string;
  serviceType?: string;
  // The service this time is for. With specialistId it says whose diary a
  // booking link holds it in — a lab draw and a doctor's visit are two.
  serviceId?: string;
  duration?: number;
  available?: boolean;
};

const formatFileSize = (size?: number | null) => {
  if (!size || size <= 0) return 'Unknown size';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  if (size < 1024 * 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  return `${(size / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const formatDob = (dob?: string | null) => {
  if (!dob) return null;
  const dateOnlyMatch = dob.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnlyMatch) {
    const year = Number(dateOnlyMatch[1]);
    const month = Number(dateOnlyMatch[2]);
    const day = Number(dateOnlyMatch[3]);
    return new Date(year, month - 1, day).toLocaleDateString('en-US');
  }
  const parsed = new Date(dob);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toLocaleDateString('en-US');
  }
  return dob;
};

const formatDateInputValue = (date: Date) => date.toLocaleDateString('en-CA');

const formatTimeFromStartTime = (startTime?: string | null) => {
  if (!startTime) return '';
  try {
    const timePart = startTime.includes('T') ? startTime.split('T')[1] : startTime;
    const [hoursRaw, minutesRaw] = timePart.split(':');
    const hours = Number(hoursRaw);
    const minutes = Number(minutesRaw || 0);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return '';
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  } catch {
    return '';
  }
};

const getSlotKey = (slot: any, index: number) =>
  slot?.id || `${slot?.date || 'unknown'}|${slot?.time || ''}|${slot?.specialist || ''}|${index}`;

const parseSlotDate = (value?: string | null) => {
  if (!value) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatSlotLabel = (slot: any, pref?: string | null) => {
  if (!slot) return 'Slot unavailable';
  const parsedDate = parseSlotDate(slot.date);
  const dateText =
    parsedDate && !Number.isNaN(parsedDate.getTime())
      ? formatDateOnly(parsedDate, pref)
      : slot.date || 'Date TBD';
  // Was `slot.time` untouched, so every slot read "13:00" — in the panel, on
  // the Booked line, and in the message the office texts the patient.
  const timeText = slot.time ? formatClockString(slot.time, pref) : 'Time TBD';
  const specialistText = slot.specialist ? ` - ${slot.specialist}` : '';
  return `${dateText} at ${timeText}${specialistText}`;
};


const normalizeManualSlot = (
  slot: SchedulingSlot,
  fallbackDate: string,
  mapping: ServiceProviderMapping | null
) => {
  const normalized: SchedulingSlot = { ...slot };
  if (!normalized.date) {
    if (slot.startTime && slot.startTime.includes('T')) {
      normalized.date = slot.startTime.split('T')[0];
    } else if (fallbackDate) {
      normalized.date = fallbackDate;
    }
  }
  if (!normalized.time) {
    normalized.time = slot.timeDisplay || formatTimeFromStartTime(slot.startTime) || slot.time || '';
  }
  if (!normalized.specialist && mapping?.specialist?.name) {
    normalized.specialist = mapping.specialist.name;
  }
  if (!normalized.specialistId && mapping?.specialist?.id) {
    normalized.specialistId = mapping.specialist.id;
  }
  if (!normalized.serviceType && mapping?.service?.name) {
    normalized.serviceType = mapping.service.name;
  }
  if (!normalized.serviceId && mapping?.service?.id) {
    normalized.serviceId = mapping.service.id;
  }
  if (!normalized.duration && mapping?.service?.duration) {
    normalized.duration = mapping.service.duration;
  }
  return normalized;
};

// Hover help on the scheduling actions. No visible icon — this panel is dense
// already, and a row of info dots would compete with the controls. The span is
// not decoration: a disabled MUI Button fires no pointer events, so a tooltip
// on Send or Book would never appear without something around it to catch them.
// The span exists so the tooltip still fires over a disabled control. It has to
// be inline-flex beside a button and block inside a menu — an inline wrapper
// lays the menu items out in a row.
const ActionHint: React.FC<{ title: string; children: React.ReactElement; block?: boolean }> = ({
  title,
  children,
  block,
}) => (
  <Tooltip title={title} placement={block ? 'left' : 'top'} arrow enterDelay={400} enterNextDelay={200}>
    <span style={{ display: block ? 'block' : 'inline-flex' }}>{children}</span>
  </Tooltip>
);

const InfoTooltipIcon: React.FC<{ title: string }> = ({ title }) => (
  <Tooltip title={title} placement="top" arrow>
    <IconButton size="small" aria-label="info" sx={{ p: 0.25 }}>
      <InfoIcon fontSize="inherit" />
    </IconButton>
  </Tooltip>
);

const normalizeContact = (contact: any): Contact => {
  const firstName = contact.first_name || contact.firstName || '';
  const lastName = contact.last_name || contact.lastName || '';
  const fullName = contact.full_name || contact.fullName || `${firstName} ${lastName}`.trim();
  return {
    id: contact.id,
    internalId: contact.internal_id || contact.internalId,
    spruceContactId: contact.spruce_contact_id || contact.spruceContactId,
    firstName,
    lastName,
    fullName: fullName || 'Unknown',
    email: contact.email,
    phone: contact.phone || contact.phone_number || contact.phoneNumber,
    dateOfBirth: contact.date_of_birth || contact.dateOfBirth,
    gender: contact.gender,
    status: contact.status || 'active',
    isArchived: contact.isArchived || false,
    tags: contact.tags || [],
    createdAt: contact.createdAt || contact.created_at || '',
    updatedAt: contact.updatedAt || contact.updated_at || '',
  } as Contact;
};

const extractPhoneCandidates = (text?: string | null) => {
  if (!text) return [] as string[];
  const matches = text.match(/(\+?1[\s.-]?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}/g);
  if (!matches) return [];
  const normalized = matches
    .map((match) => match.replace(/\D/g, ''))
    .filter(Boolean)
    .map((digits) => (digits.length > 10 ? digits.slice(-10) : digits));
  return Array.from(new Set(normalized));
};

const splitPatientName = (name?: string | null) => {
  const trimmed = (name || '').trim();
  if (!trimmed) return { firstName: '', lastName: '' };
  const parts = trimmed.split(/\s+/);
  const firstName = parts.shift() || '';
  const lastName = parts.join(' ');
  return { firstName, lastName };
};

const isUnknownName = (name?: string | null) => {
  if (!name) return true;
  const lower = name.toLowerCase();
  return lower.includes('unknown') || lower.includes('unidentified');
};

const TaskDetailsModal: React.FC<TaskDetailsModalProps> = ({
  open,
  task,
  onClose,
  onUpdate,
  initialResponse,
  onDeleted,
  onOpenChart,
}) => {
  // Full-screen dialog on mobile (down('md')) so the task fills the phone.
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const createSplitRow = () => ({
    id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    startPage: '',
    endPage: '',
  });
  const [response, setResponse] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [notesOpen, setNotesOpen] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [noteBusyId, setNoteBusyId] = useState<string | null>(null);
  // Deleting a note used to ask through window.confirm. A browser that
  // declines to show that returns false, which reads as "no" — so Delete did
  // nothing at all, silently. Same defect as the calendar and Admin had.
  const [noteToDelete, setNoteToDelete] = useState<InternalNote | null>(null);
  // Deleting the task itself, offered only for the ones the system wrote.
  const [confirmDeleteTask, setConfirmDeleteTask] = useState(false);
  const [deletingTask, setDeletingTask] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);
  const [historyAnchor, setHistoryAnchor] = useState<HTMLElement | null>(null);
  const [historyNote, setHistoryNote] = useState<InternalNote | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [responseId, setResponseId] = useState<string | null>(null);
  const [pendingAttachments, setPendingAttachments] = useState<MessageAttachment[]>([]);
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isAiCollapsed, setIsAiCollapsed] = useState(true);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignees, setAssignees] = useState<Array<{ id: string | number; name?: string; email?: string }>>([]);
  const [assignLoading, setAssignLoading] = useState(false);
  const [selectedAssigneeId, setSelectedAssigneeId] = useState<string | number>('');
  const [assignError, setAssignError] = useState<string | null>(null);
  const [followUpDialogOpen, setFollowUpDialogOpen] = useState(false);
  const [followUpDate, setFollowUpDate] = useState(formatDateInputValue(new Date()));
  const [followUpLoading, setFollowUpLoading] = useState(false);
  const [followUpError, setFollowUpError] = useState<string | null>(null);
  const [isEditingPatient, setIsEditingPatient] = useState(false);
  const [patientFirstName, setPatientFirstName] = useState('');
  const [patientLastName, setPatientLastName] = useState('');
  const [patientSaveLoading, setPatientSaveLoading] = useState(false);
  const [patientSaveError, setPatientSaveError] = useState<string | null>(null);
  const [resolvedPhone, setResolvedPhone] = useState<{ phone: string | null; label: string | null } | null>(null);
  const [patientToast, setPatientToast] = useState<string | null>(null);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [mergePrimaryId, setMergePrimaryId] = useState<string | null>(null);
  const [mergeCandidates, setMergeCandidates] = useState<Array<{
    id: string;
    fullName?: string;
    phone?: string;
    isArchived?: boolean;
  }>>([]);
  const [mergeSelection, setMergeSelection] = useState<string[]>([]);
  const [mergeLoading, setMergeLoading] = useState(false);
  const [mergeError, setMergeError] = useState<string | null>(null);
  const [attachmentPreview, setAttachmentPreview] = useState<{
    url: string;
    filename: string;
    contentType?: string;
    transcription?: string;
    isObjectUrl?: boolean;
  } | null>(null);
  const [attachmentPreviewText, setAttachmentPreviewText] = useState('');
  const [attachmentPreviewSource, setAttachmentPreviewSource] = useState<{
    attachment: MessageAttachment;
    message: any;
  } | null>(null);
  const [taskDetails, setTaskDetails] = useState<Task | null>(null);
  const [selectedLabFaxId, setSelectedLabFaxId] = useState<string | null>(null);
  const [labFaxDetail, setLabFaxDetail] = useState<UnfiledLabFax | null>(null);
  const [labFaxLoading, setLabFaxLoading] = useState(false);
  const [labSearchQuery, setLabSearchQuery] = useState('');
  const [labSearchResults, setLabSearchResults] = useState<Contact[]>([]);
  const [labSuggestedContacts, setLabSuggestedContacts] = useState<Contact[]>([]);
  const [labSearchLoading, setLabSearchLoading] = useState(false);
  const [labAttachLoading, setLabAttachLoading] = useState(false);
  const [labError, setLabError] = useState<string | null>(null);
  const [labAttachToast, setLabAttachToast] = useState<string | null>(null);
  const [showSplitPanel, setShowSplitPanel] = useState(false);
  const [labSplitRows, setLabSplitRows] = useState<Array<{ id: string; startPage: string; endPage: string }>>([
    createSplitRow(),
  ]);
  const [labSplitLoading, setLabSplitLoading] = useState(false);
  const [labSplitError, setLabSplitError] = useState<string | null>(null);
  const [taskActionsAnchor, setTaskActionsAnchor] = useState<HTMLElement | null>(null);
  // What a poll found that the user did not do themselves. Cleared as soon as
  // they touch anything, so it marks a change rather than nagging about one.
  const [liveChange, setLiveChange] = useState('');
  // A time the patient chose through their own link. It is held as 'tentative'
  // on purpose — a person still decides — but until now nothing in the app
  // could move it off tentative, so the task said "needs confirming" and
  // offered no way to confirm.
  const [tentativeBooking, setTentativeBooking] = useState<{ id: string; startTime: string; provider: string } | null>(null);
  const [confirmingBooking, setConfirmingBooking] = useState(false);
  const [tellPatientOnConfirm, setTellPatientOnConfirm] = useState(false);
  // One box now. The scheduling panel's own "message the patient will get"
  // was merged into the reply box below Conversation History, which is how
  // every other compose surface in the app already works. The panel keeps its
  // structure and writes into that box; lastGeneratedDraftRef is its ownership
  // marker, so machine-written text can be replaced until a person edits it.
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const replyBoxRef = useRef<HTMLDivElement | null>(null);
  // Effects read the box through a ref rather than a dependency, so they can
  // check ownership without re-running on every keystroke.
  const responseRef = useRef('');
  // Set by Add to the offer; consumed by the effect that writes the times, and
  // only when the write actually happened.
  const revealAfterSlotWriteRef = useRef<string | null>(null);
  const [aiSchedulingLoading, setAiSchedulingLoading] = useState(false);
  const [aiSchedulingError, setAiSchedulingError] = useState<string | null>(null);
  const [aiSchedulingOverrideSlots, setAiSchedulingOverrideSlots] = useState<Array<{ key: string; slot: SchedulingSlot }>>([]);
  const [aiSchedulingOverrideTouched, setAiSchedulingOverrideTouched] = useState(false);
  const [bookingSlotKey, setBookingSlotKey] = useState<string | null>(null);
  // Bumped after a booking so the offered times are fetched again — the time
  // just taken must stop being offered on the screen that took it.
  const [slotReloadKey, setSlotReloadKey] = useState(0);
  // url is empty when the link was rehydrated from the task on reopen — the
  // token is shown exactly once, at creation, and is not stored anywhere.
  const [bookingLink, setBookingLink] = useState<{
    id: string; url: string; expiresAt: string; ttlDays?: number; createdAt?: string;
    heldSlots?: Array<{ date: string; time: string; durationMinutes?: number; serviceName?: string; providerName?: string }>;
  } | null>(null);
  // Named apart from the patient-link picker's linkBusy, which is a different
  // kind of link entirely.
  const [bookingLinkBusy, setBookingLinkBusy] = useState(false);
  // Which link the slot grid should look past. A ref so the fetch can read it
  // without the effect re-running on every rehydrate.
  const bookingLinkIdRef = useRef<string | null>(null);
  // What holding the chosen times would leave of each day, from the server.
  const [holdImpact, setHoldImpact] = useState<HoldImpact>({ level: 'none', message: null });
  const [linkCopied, setLinkCopied] = useState(false);
  const bookingLinkFieldRef = useRef<HTMLInputElement | null>(null);
  // null while unknown. The link block stays hidden until we know the practice
  // has this on — offering to make a link that cannot be opened is worse than
  // not offering one, because the office would send it believing it worked.
  const [selfBookingEnabled, setSelfBookingEnabled] = useState<boolean | null>(null);
  // How long a new link will hold its times — the practice's setting, so the
  // hint on the button says the real deadline. It said "a week" after the
  // practice had moved to a day.
  const [linkHoldDays, setLinkHoldDays] = useState(1);
  const conversationScrollRef = useRef<HTMLDivElement | null>(null);
  const [bookedAppointment, setBookedAppointment] = useState<{ id: string; label: string; bookedAt: string; earlier?: string | null } | null>(null);
  // A set booked from the panel one step at a time: what has been booked in
  // this sitting (for one reply naming all of it) and whether steps remain.
  const [bookedThisSession, setBookedThisSession] = useState<BookedForReply[]>([]);
  const [stepsLeft, setStepsLeft] = useState(false);
  const [isRescheduleMode, setIsRescheduleMode] = useState(false);
  const [slotOverrideMappings, setSlotOverrideMappings] = useState<ServiceProviderMapping[]>([]);
  const [slotOverrideMappingId, setSlotOverrideMappingId] = useState('');
  // The rules behind the set buttons, for the warning under the offer; and
  // what each button last proposed, so "Another option" knows where to go on from.
  const [bundleRules, setBundleRules] = useState<BundleRule[]>([]);
  const [lastProposals, setLastProposals] = useState<Record<string, Array<{ date: string }>>>({});
  // Which set button is waiting on the server. A proposal walks the calendar
  // day by day and can take ten seconds; with no sign of it, the button was
  // pressed again or given up on.
  const [proposingBundle, setProposingBundle] = useState<string | null>(null);
  const [slotOverrideDate, setSlotOverrideDate] = useState(formatDateInputValue(new Date()));
  // Whether the practice takes same-day bookings, so the panel can open on a
  // day that is actually bookable rather than on today.
  const [sameDayAllowed, setSameDayAllowed] = useState<boolean | null>(null);
  // Only move the date for the office once — after that the date is theirs.
  const defaultDateSettledRef = useRef(false);
  const [slotOverrideAvailableSlots, setSlotOverrideAvailableSlots] = useState<SchedulingSlot[]>([]);
  const [slotOverrideSelectedKeys, setSlotOverrideSelectedKeys] = useState<string[]>([]);
  // How much availability to put in front of the patient: the chosen day, or
  // the working week from that day. Staff decide per conversation.
  const [slotOverrideRange, setSlotOverrideRange] = useState<'day' | 'week'>('day');
  // A patient saying they are not coming. Read from the message; the office
  // decides what to do about it.
  const [cancellation, setCancellation] = useState<{
    looksLikeCancellation: boolean;
    kind?: 'cancel' | 'reschedule' | 'cannot-make-it' | null;
    upcomingAppointments: Array<{
      id: string; patientName: string; startTime: string; endTime: string;
      appointmentType?: string; provider?: string; duration?: number;
    }>;
  } | null>(null);
  const [chosenAppointment, setChosenAppointment] = useState<string>('');
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancellationDismissed, setCancellationDismissed] = useState(false);
  const [slotOverrideLoading, setSlotOverrideLoading] = useState(false);
  const dateTimeFormat = useAuthStore((state) => state.user?.dateTimeFormat);
  const currentUserId = useAuthStore((state) => state.user?.id ?? null);
  const isAdmin = useAuthStore((state) => {
    const roles = state.user?.roles || [];
    return roles.some((r: any) => {
      const name = String(typeof r === 'string' ? r : r?.name || '').toLowerCase();
      return name === 'admin' || name === 'superuser' || name === 'super_admin';
    });
  });
  const [slotOverrideError, setSlotOverrideError] = useState<string | null>(null);
  // The dropdown-and-date row, so "Add a time for this step" can bring it
  // into view: it sits above the offer, out of sight of that button.
  const pickerRef = useRef<HTMLDivElement | null>(null);
  // A time that is not in the list (10:15, say). Picked from three lists —
  // hour, minutes, AM or PM — not typed: nothing to misspell, and it is how
  // staff already choose a time elsewhere. Then what it was told when the
  // time could not be offered, and whether the calendar is being asked.
  const [pickedHour, setPickedHour] = useState(9);
  const [pickedMinute, setPickedMinute] = useState('00');
  const [pickedPeriod, setPickedPeriod] = useState<'AM' | 'PM'>('AM');
  const [typedTimeNote, setTypedTimeNote] = useState<string | null>(null);
  const [typedTimeBusy, setTypedTimeBusy] = useState(false);
  // Folded away until asked for: most offers are made from the list, and
  // controls that are always there read as something that must be filled in.
  const [typedTimeOpen, setTypedTimeOpen] = useState(false);
  // "The last link expired … without a booking", when that is what happened.
  const [lapsedLink, setLapsedLink] = useState<string | null>(null);
  // A refusal is about one day and one provider. Change either and it is no
  // longer true — "10:20 AM is not free" stayed on screen under a new date.
  useEffect(() => { setTypedTimeNote(null); }, [slotOverrideDate, slotOverrideMappingId]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const lastSchedulingTaskIdRef = useRef<string | null>(null);
  const lastSchedulingSeedRef = useRef<string>('');
  // The exact string we last wrote into the patient-message box. While the
  // box still holds it, nobody has typed and we may regenerate freely.
  const lastGeneratedDraftRef = useRef<string | null>(null);

  const taskId = (task as any)?.id || (task as any)?._id;

  const buildPatientDetailsUrl = () => {
    // Prefer the canonical internal LMD id (`internalPatientId`); the patient
    // route resolves it to the Spruce key via the contacts master. Fall back to
    // the Spruce id so existing contacts keep working.
    const patientKey = task?.internalPatientId || task?.patientId;
    if (!patientKey) return null;
    const params = new URLSearchParams();
    if (task?.patientName) params.set('name', task.patientName);
    if (task?.patientPhone) params.set('phone', task.patientPhone);
    if (task?.patientEmail) params.set('email', task.patientEmail);
    const query = params.toString();
    return `/patient-details/${patientKey}${query ? `?${query}` : ''}`;
  };

  const handleOpenPatientDetails = () => {
    const url = buildPatientDetailsUrl();
    if (!url) {
      // Previously this was a silent no-op when a task had neither an LMD id nor
      // a Spruce id, which is why the button "did nothing" for some patients.
      // Give explicit feedback instead.
      setPatientToast('No patient identifier is linked to this conversation yet, so the chart cannot be opened.');
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Full patient profile in the same tab — routes into the Contacts page (the
  // one true patient design: hero + Details / AI / Messages / Documents /
  // Billing / History) with the patient auto-selected and a "Back to task"
  // button that reopens this task's dock via the dashboard's ?taskId= deep
  // link. Tasks with only a Spruce id (no LMD contact) fall back to the
  // legacy standalone page. The chart drawer stays the quick peek.
  const handleOpenProfile = () => {
    const returnState = { returnTo: `/?taskId=${taskId}`, backLabel: 'Back to task' };
    if (task?.internalPatientId) {
      navigate('/contacts', { state: { ...returnState, selectInternalId: task.internalPatientId } });
      return;
    }
    const url = buildPatientDetailsUrl();
    if (!url) {
      setPatientToast('No patient identifier is linked to this conversation yet, so the profile cannot be opened.');
      return;
    }
    navigate(url, { state: returnState });
  };

  // In-place chart: same id gate as buildPatientDetailsUrl. Falls back to the
  // legacy new-tab flow when the parent doesn't host a chart surface.
  const chartKey = task?.internalPatientId || task?.patientId;
  const handleOpenChart = () => {
    if (onOpenChart && chartKey) {
      onOpenChart({
        spruceEntityId: chartKey,
        name: (taskDetails || task)?.patientName,
        phone: task?.patientPhone,
        email: task?.patientEmail,
      });
      return;
    }
    handleOpenPatientDetails();
  };

  // Link/relink the task to a different existing patient (identity only —
  // the backend never touches the sender phone or message thread).
  const [linkPickerOpen, setLinkPickerOpen] = useState(false);
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const handleRelinkPatient = async (p: SearchPatient) => {
    if (!taskId) return;
    setLinkBusy(true);
    setLinkError(null);
    try {
      const response = await triageApi.relinkPatient(taskId, {
        name: p.name,
        internalId: p.internalId || undefined,
        // Note: the search result's patientId is the internal contact UUID,
        // NOT a Spruce entity id — only pass a real Spruce id here.
        spruceContactId: p.spruceContactId || undefined,
        contactId: p.id,
        phone: p.phone,
        email: p.email,
        dateOfBirth: p.dateOfBirth,
        gender: p.gender,
      });
      const returned = response.data?.data;
      const updatedTask: Task = {
        ...(displayTask as Task),
        patientName: returned?.patientName ?? p.name,
        patientId: returned?.patientId ?? (p.spruceContactId || p.patientId),
        internalPatientId: returned?.internalPatientId ?? p.internalId,
        spruceContactId: returned?.spruceContactId ?? (displayTask as Task)?.spruceContactId,
        associatedContactIds: returned?.associatedContactIds ?? (displayTask as Task)?.associatedContactIds,
      };
      setTaskDetails(updatedTask);
      onUpdate(updatedTask);
      setLinkPickerOpen(false);
      setPatientToast('Patient link updated.');
    } catch (err: any) {
      setLinkError(err?.response?.data?.error || err?.message || 'Failed to link patient');
    } finally {
      setLinkBusy(false);
    }
  };

  const fetchTaskDetails = useCallback(async () => {
    if (!taskId) return null;
    try {
      const response = await triageApi.getTask(taskId);
      const data = response.data?.data || response.data?.task || response.data;
      if (!data) return null;
      return {
        ...data,
        id: data.id || data._id || taskId,
      } as Task;
    } catch (error) {
      console.error('Failed to load task details', error);
      return null;
    }
  }, [taskId]);

  const refreshTaskDetails = async () => {
    const data = await fetchTaskDetails();
    if (data) {
      setTaskDetails(data);
    }
  };

  useEffect(() => {
    let isActive = true;
    const loadTaskDetails = async () => {
      if (!open || !taskId) {
        if (isActive) {
          setTaskDetails(null);
        }
        return;
      }
      setTaskDetails(null);
      const data = await fetchTaskDetails();
      if (isActive && data) {
        setTaskDetails(data);
      }
    };
    loadTaskDetails();
    return () => {
      isActive = false;
    };
  }, [open, taskId, fetchTaskDetails]);

  const labTask = useMemo(() => taskDetails || task, [taskDetails, task]);
  const labMatch = useMemo(() => (labTask as Task | null)?.labMatch || null, [labTask]);
  const unfiledLabFaxes = useMemo(() => (labTask as Task | null)?.unfiledLabFaxes || [], [labTask]);

  useEffect(() => {
    if (!open) return;
    if (!unfiledLabFaxes.length) {
      setSelectedLabFaxId(null);
      setLabFaxDetail(null);
      return;
    }
    if (!selectedLabFaxId) {
      setSelectedLabFaxId(unfiledLabFaxes[0].id);
    }
  }, [open, unfiledLabFaxes, selectedLabFaxId]);

  useEffect(() => {
    const loadLabDetail = async () => {
      if (!selectedLabFaxId) {
        setLabFaxDetail(null);
        return;
      }
      setLabFaxLoading(true);
      setLabError(null);
      try {
        const response = await unfiledLabsApi.get(selectedLabFaxId);
        const data = response.data?.data || response.data || null;
        setLabFaxDetail(data || null);
      } catch (err: any) {
        setLabError(err?.message || 'Failed to load lab fax details');
      } finally {
        setLabFaxLoading(false);
      }
    };
    loadLabDetail();
  }, [selectedLabFaxId]);

  useEffect(() => {
    setLabSplitRows([createSplitRow()]);
    setLabSplitError(null);
    setShowSplitPanel(false);
  }, [selectedLabFaxId]);

  useEffect(() => {
    const runSuggestions = async () => {
      if (!labFaxDetail?.ocr_text) {
        setLabSuggestedContacts([]);
        return;
      }
      const candidates = extractPhoneCandidates(labFaxDetail.ocr_text);
      if (!candidates.length) {
        setLabSuggestedContacts([]);
        return;
      }
      try {
        setLabSearchLoading(true);
        const results: Contact[] = [];
        for (const phone of candidates) {
          const response = await contactsApi.searchContacts(phone);
          const data = response.data?.data || response.data || [];
          if (Array.isArray(data)) {
            data.forEach((item) => results.push(normalizeContact(item)));
          }
        }
        const seen = new Set<string>();
        const unique = results.filter((item) => {
          if (seen.has(item.id)) return false;
          seen.add(item.id);
          return true;
        });
        setLabSuggestedContacts(unique);
      } catch (err) {
        setLabSuggestedContacts([]);
      } finally {
        setLabSearchLoading(false);
      }
    };
    runSuggestions();
  }, [labFaxDetail?.ocr_text]);

  const displayTask = taskDetails || task;
  const aiScheduling = (displayTask as any)?.aiScheduling || null;
  const showSchedulingPanel = Boolean(aiScheduling) || displayTask?.category === 'appointment';

  // The scheduling ask is in the newest thing the patient said, not in the
  // message that opened the thread — which on a long thread can be months old.
  const latestPatientMessage = useMemo(() => {
    const msgs: any[] = (displayTask as any)?.conversationMessages || [];
    const latestInbound = [...msgs].reverse().find((m) => m?.direction === 'inbound' && m?.content);
    return String(latestInbound?.content || (displayTask as any)?.messageText || '');
  }, [displayTask]);
  // The window the patient asked for, from the most recent of their messages
  // that names one — not only the latest, which is often just "Yes!".
  const requestedWindow = useMemo(
    () => requestedWindowFromThread((displayTask as any)?.conversationMessages, (displayTask as any)?.messageText),
    [displayTask],
  );
  const requestedWindowKey = requestedWindow ? `${requestedWindow.start.getTime()}|${requestedWindow.end.getTime()}` : '';

  useEffect(() => {
    defaultDateSettledRef.current = false;
    if (!displayTask) return;
    // Clear the per-task draft state when switching patients so a previous
    // patient's typed/AI-suggested response, note, and pending attachments
    // don't bleed into the newly opened task.
    // Normally cleared when switching patients, so one person's draft cannot
    // bleed into another's. A note handed over from a cancellation is the
    // exception: it is FOR this patient and is why the task was opened.
    // The reply box itself is seeded by the scheduling effect below, which
    // knows about both the cancellation hand-off and the AI draft. Seeding it
    // here as well raced that effect and lost the hand-off.
    setResponseId(null);
    setPendingAttachments([]);
    setInternalNote('');
    const { firstName, lastName } = splitPatientName(displayTask.patientName);
    setPatientFirstName(firstName);
    setPatientLastName(lastName);
    setIsAiCollapsed(true);
    const assignedId = (displayTask as Task)?.assignedToUser?.id ?? (displayTask as Task)?.assignedTo ?? '';
    setSelectedAssigneeId(assignedId || '');
    if ((displayTask as Task)?.followUpDate) {
      const parsed = parseSlotDate((displayTask as Task).followUpDate || null);
      if (parsed) {
        setFollowUpDate(formatDateInputValue(parsed));
      }
    } else {
      setFollowUpDate(formatDateInputValue(new Date()));
    }
    setAssignError(null);
    setFollowUpError(null);
    setPatientSaveError(null);
    setIsEditingPatient(false);
  }, [displayTask?.id]);

  const handleClose = () => {
    setIsEditingPatient(false);
    setPatientSaveError(null);
    onClose();
  };

  // Task 3: resolve a display phone (any Spruce number type incl fax) when the task has none.
  useEffect(() => {
    setResolvedPhone(null);
    const t = displayTask as Task | undefined;
    if (!t?.id) return;
    const existing = t.patientPhone;
    if (existing && existing !== 'Unknown') return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await triageApi.resolveContactPhone(t.id);
        const data = resp.data?.data || resp.data;
        if (!cancelled && data && data.phone) {
          setResolvedPhone({ phone: data.phone, label: data.label || null });
        }
      } catch (e) {
        if (!cancelled) setResolvedPhone(null);
      }
    })();
    return () => { cancelled = true; };
  }, [displayTask?.id]);
  const schedulingStatus = aiScheduling?.status || 'draft';
  const schedulingStatusLabel = schedulingStatus === 'pending_approval'
    ? 'Needs Approval'
    : schedulingStatus === 'slots_sent'
      ? 'Sent to Patient'
      : schedulingStatus === 'no_slots_available'
        ? 'Sent: No Availability'
        : schedulingStatus === 'appointment_confirmed'
          ? 'Confirmed'
          : schedulingStatus === 'appointment_cancelled'
            ? 'Cancelled'
          : schedulingStatus === 'rejected'
            ? 'Rejected'
            : 'Draft';
  const schedulingStatusColor = schedulingStatus === 'pending_approval' || schedulingStatus === 'appointment_cancelled'
    ? 'warning'
    : schedulingStatus === 'appointment_confirmed'
      ? 'success'
      : schedulingStatus === 'rejected'
        ? 'default'
        : 'info';
  const isConfirmationDraft = aiScheduling?.pendingType === 'confirmation';
  // When the time picker is on screen. A booked patient's task shows it only
  // after "Offer different times" (or just after a booking made here, whose
  // "Booked —" box lives inside it). It used to show on a link-booked task
  // with no button pressed — and wipe whatever was picked — so there were two
  // ways in and neither worked.
  const showTimePicker = aiScheduling?.status === 'appointment_confirmed'
    ? (isRescheduleMode || Boolean(bookedAppointment))
    : (!isConfirmationDraft || isRescheduleMode);
  const slotOptions = useMemo<Array<{ slot: SchedulingSlot; key: string; label: string }>>(() => {
    const slots = (aiScheduling?.availableSlots || []) as SchedulingSlot[];
    return slots.map((slot, idx) => ({
      slot,
      key: getSlotKey(slot, idx),
      label: formatSlotLabel(slot, dateTimeFormat),
    }));
  }, [aiScheduling?.availableSlots, dateTimeFormat]);
  // The offer as steps, and what the set rules say about it — hooks, so they
  // sit with the others, above the early returns.
  const offerGroups = useMemo(() => offerSteps(aiSchedulingOverrideSlots as any), [aiSchedulingOverrideSlots]);
  // Two things the office is warned about: a set whose rule a patient could
  // break, and a time before the window the patient asked for. Both are
  // warnings, never gates — the office may know what the patient does not.
  const offerWarnings = useMemo(
    () => [
      ...bundleWarnings(aiSchedulingOverrideSlots as any, bundleRules),
      ...windowWarnings(
        aiSchedulingOverrideSlots.map((item: any) => item.slot),
        requestedWindow,
        displayTask?.patientName,
      ),
    ],
    [aiSchedulingOverrideSlots, bundleRules, requestedWindow, displayTask?.patientName],
  );
  const selectedMapping = useMemo(
    () => slotOverrideMappings.find((mapping) => mapping.id === slotOverrideMappingId) || null,
    [slotOverrideMappings, slotOverrideMappingId]
  );
  const pickingNote = pickingForStep(offerGroups, selectedMapping);
  const manualSlotOptions = useMemo<Array<{ slot: SchedulingSlot; key: string; label: string }>>(() => {
    const normalizedSlots = slotOverrideAvailableSlots
      .filter((slot) => slot.available !== false)
      .map((slot) => normalizeManualSlot(slot, slotOverrideDate, selectedMapping));
    return normalizedSlots.map((slot, idx) => ({
      slot,
      key: getSlotKey(slot, idx),
      label: formatSlotLabel(slot, dateTimeFormat),
    }));
  }, [slotOverrideAvailableSlots, slotOverrideDate, selectedMapping, dateTimeFormat]);

  // Grouped by day so the panel reads like a calendar rather than a list to
  // scroll: staff wanted to see the options without leaving the task.
  const slotOptionsByDay = useMemo(() => {
    const groups = new Map<string, Array<{ slot: SchedulingSlot; key: string; label: string }>>();
    manualSlotOptions.forEach((option) => {
      const day = option.slot.date || slotOverrideDate;
      if (!groups.has(day)) groups.set(day, []);
      groups.get(day)!.push(option);
    });
    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [manualSlotOptions, slotOverrideDate]);

  useEffect(() => {
    if (schedulingStatus !== 'appointment_confirmed' && isRescheduleMode) {
      setIsRescheduleMode(false);
    }
  }, [schedulingStatus, isRescheduleMode]);

  useEffect(() => {
    const taskKey = displayTask?.id ? String(displayTask.id) : null;
    const draftMessage = draftForBox(aiScheduling);
    const seed = `${aiScheduling?.status || ''}|${aiScheduling?.pendingType || ''}|${draftMessage}`;

    if (taskKey && lastSchedulingTaskIdRef.current !== taskKey) {
      lastSchedulingTaskIdRef.current = taskKey;
      lastSchedulingSeedRef.current = seed;
      // A note handed over from a cancellation is a person's words and is why
      // the task was opened, so it wins and counts as human-written (ref null).
      // Otherwise the AI seed, which counts as generated (ref set) and can be
      // replaced by the slot picker until somebody edits it.
      const first = initialResponse || draftMessage;
      lastGeneratedDraftRef.current = initialResponse ? null : (draftMessage || null);
      setResponse(first);
      setSendError(null);
      setAiSchedulingOverrideTouched(false);
      setIsRescheduleMode(false);
      setAiSchedulingError(null);
      setBookedAppointment(null);
      setBookedThisSession([]);
      setStepsLeft(false);
      // A link belongs to the patient it was made for. Without this it stayed
      // in state across a task switch, so one patient's live link appeared on
      // the next patient's panel — offering a Withdraw that would have
      // revoked somebody else's link, and hiding the create button for a task
      // that had no link at all. The modal is not remounted between tasks, so
      // nothing else clears it.
      setBookingLink(null);
      setLapsedLink(null);
      setLinkCopied(false);
      return;
    }

    if (seed && seed !== lastSchedulingSeedRef.current) {
      lastSchedulingSeedRef.current = seed;
      // This branch used to overwrite the box whenever the server-side
      // scheduling state moved. That was survivable while it only ran after the
      // user's own action; with the task polling, a patient booking mid-sentence
      // would delete what a staff member was writing to them. Same rule the slot
      // picker already follows: their words are theirs.
      if (!canReplaceDraft(responseRef.current, lastGeneratedDraftRef.current)) return;
      lastGeneratedDraftRef.current = draftMessage || null;
      setResponse(draftMessage);
      setAiSchedulingOverrideTouched(false);
      setIsRescheduleMode(false);
      setAiSchedulingError(null);
    }
  }, [
    aiScheduling?.draftMessage,
    aiScheduling?.message,
    aiScheduling?.status,
    aiScheduling?.pendingType,
    displayTask?.id,
    initialResponse,
  ]);

  useEffect(() => {
    if (!open || !showSchedulingPanel) return;
    const loadMappings = async () => {
      try {
        const response = await serviceMappingApi.getAll();
        const mappings = response.data?.mappings || response.data?.data?.mappings || [];
        setSlotOverrideMappings(Array.isArray(mappings) ? mappings : []);
        schedulingApi.getBundles()
          .then((r) => setBundleRules(Array.isArray(r.data?.data) ? r.data.data : []))
          .catch(() => setBundleRules([]));
        if (!slotOverrideMappingId && Array.isArray(mappings) && mappings.length > 0) {
          // Was mappings[0] — the first row the API happened to return, so a
          // phone call with the doctor opened on a lab draw with the nurse, on
          // every task. Getting this wrong costs one change of the dropdown;
          // it books nothing.
          // A wellness recall opens on the lab draw, not on a guess from the
          // message: the sequence starts with the lab, and the lab has the
          // tighter placement rule (mornings, Mon-Thu), so it is the step a
          // self-serve link earns its keep on. The visit with the doctor is
          // proposed afterwards with the Wellness exam button.
          const recall = (displayTask as any)?.source === 'wellness_recall';
          const lab = recall ? mappings.find((m: any) => /lab|draw/i.test(String(m?.service?.name || ''))) : null;
          setSlotOverrideMappingId(lab?.id || suggestServiceMapping(latestPatientMessage, mappings) || mappings[0].id);
        }
        if (Array.isArray(mappings) && mappings.length === 0) {
          setSlotOverrideMappingId('');
        }
      } catch (err) {
        setSlotOverrideError('Failed to load provider mappings');
      }
    };
    loadMappings();
  }, [open, showSchedulingPanel, slotOverrideMappingId, latestPatientMessage]);

  // The panel used to open on today. With same-day booking off — which is how
  // the practice runs — today can never offer a time, so it greeted everybody
  // with "No available slots for this date" and waited for them to know to
  // change it.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await aiSchedulingConfigApi.get();
        const config = res.data?.data || res.data || {};
        if (!cancelled) setSelfBookingEnabled(Boolean(config.enablePatientSelfBooking));
        try {
          const ttl = await settingsApi.get('scheduling_link_ttl_days');
          if (!cancelled) setLinkHoldDays(holdDays(ttl.data?.setting?.value));
        } catch {
          // Unreadable: the server falls back to a day, so the hint does too.
        }
      } catch {
        // Unreadable means we cannot promise the link will open, so the block
        // stays hidden.
        if (!cancelled) setSelfBookingEnabled(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!open) return;
    const el = conversationScrollRef.current;
    if (!el) return;
    const toBottom = () => { el.scrollTop = el.scrollHeight; };
    toBottom();
    const frame = window.requestAnimationFrame(toBottom);
    const timers = [80, 250].map((ms) => window.setTimeout(toBottom, ms));
    return () => {
      window.cancelAnimationFrame(frame);
      timers.forEach((id) => window.clearTimeout(id));
    };
  }, [open, displayTask?.id, displayTask?.conversationMessages, (displayTask as any)?.internalNotes]);



  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await settingsApi.get('scheduling_same_day_allowed');
        if (!cancelled) setSameDayAllowed(res.data?.setting?.value === true);
      } catch (err) {
        // Unreadable setting: assume the stricter of the two, so the panel
        // still opens on a day it can book.
        if (!cancelled) setSameDayAllowed(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (defaultDateSettledRef.current) return;
    if (sameDayAllowed === null || !selectedMapping) return;
    defaultDateSettledRef.current = true;
    const window = requestedWindow;
    const from = window ? window.start : new Date();
    setSlotOverrideDate(
      nextBookableDate(
        from,
        (selectedMapping as any)?.specialist?.workingDays,
        Boolean(sameDayAllowed || window),
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sameDayAllowed, selectedMapping, displayTask?.id, requestedWindowKey]);


  useEffect(() => {
    // Each change of mapping, date or range starts a new fetch while the last
    // may still be in flight — and a week asks for seven days where a single
    // day asks for one, so they do not come back in the order they were sent.
    // Without this guard a stale reply can land last and paint slots that do
    // not match the controls: the panel would say "Whole week" over one day's
    // times. Not reproducible against a local server, which answers too
    // quickly to lose the race, but it is a real ordering hazard on a slower
    // connection.
    let cancelled = false;
    const fetchSlots = async () => {
      if (!selectedMapping || !slotOverrideDate) return;
      setSlotOverrideLoading(true);
      setSlotOverrideError(null);
      try {
        const dates = [slotOverrideDate];
        if (slotOverrideRange === 'week') {
          const start = new Date(`${slotOverrideDate}T00:00:00`);
          for (let i = 1; i < 7; i += 1) {
            const next = new Date(start);
            next.setDate(start.getDate() + i);
            dates.push(formatDateInputValue(next));
          }
        }
        const perDay = await Promise.all(
          dates.map(async (date) => {
            const response = await schedulingApi.getSlots(
              selectedMapping.service.id,
              selectedMapping.specialist.id,
              date,
              // The times our own live link holds are reserved for THIS
              // patient, so they belong in this patient's grid. Without this
              // the panel read "No available slots for this date" on an
              // afternoon it was holding four times for them.
              bookingLinkIdRef.current || undefined,
            );
            const responseData = response.data?.data || response.data || {};
            const slots = responseData.slots || responseData || [];
            // Carry the date on each slot: a week's worth is meaningless once
            // the slots are pooled without knowing which day they belong to.
            return (Array.isArray(slots) ? slots : []).map((slot: any) => ({ ...slot, date: slot.date || date }));
          })
        );
        if (cancelled) return;
        setSlotOverrideAvailableSlots(perDay.flat());
      } catch (err) {
        if (cancelled) return;
        setSlotOverrideError('Failed to load available slots');
        setSlotOverrideAvailableSlots([]);
      } finally {
        if (!cancelled) setSlotOverrideLoading(false);
      }
    };
    if (showSchedulingPanel && showTimePicker) {
      fetchSlots();
    }
    return () => { cancelled = true; };
    // bookingLink is read through a ref and reloaded via slotReloadKey rather
    // than listed here: putting it in the deps refetched the whole week every
    // time the link object was replaced by the rehydrate poll.
  }, [selectedMapping, slotOverrideDate, slotOverrideRange, showSchedulingPanel, showTimePicker, slotReloadKey]);

  useEffect(() => {
    // Booking ends the offer. The times that were sent still live on the task
    // as availableSlots, so without this they came straight back and the panel
    // read as though the patient were still choosing — next to a banner saying
    // it was booked.
    const action = offerSeedAction({
      status: schedulingStatus,
      booked: Boolean(bookedAppointment),
      touched: aiSchedulingOverrideTouched,
      rescheduling: isRescheduleMode,
      stepsLeft,
    });
    if (action === 'clear') {
      setAiSchedulingOverrideSlots([]);
      return;
    }
    // A list somebody is curating is theirs. The poll hands back a fresh
    // availableSlots array every 15s; re-seeding on each one dropped every
    // time that had been added by hand.
    if (action === 'keep') return;
    setAiSchedulingOverrideSlots(slotOptions.map((option) => ({ key: option.key, slot: option.slot })));
  }, [slotOptions, displayTask?.id, schedulingStatus, bookedAppointment, aiSchedulingOverrideTouched, isRescheduleMode, stepsLeft]);

  // The wording lives in slotsDraftMessage.ts, where it is a tested literal.
  // After a cancellation the times follow the "was cancelled" sentence the
  // server drafted, instead of replacing it: the patient is told why they
  // are being offered times.
  const buildSlotsDraftMessage = useCallback(
    (slots: any[]) => slotsDraftMessage(slots, dateTimeFormat, {
      lead: aiScheduling?.status === 'appointment_cancelled' ? cancellationLead(aiScheduling?.draftMessage) : '',
    }),
    [dateTimeFormat, aiScheduling?.status, aiScheduling?.draftMessage],
  );

  useEffect(() => {
    if (!aiSchedulingOverrideTouched || isConfirmationDraft) return;
    const slots = aiSchedulingOverrideSlots.map((item) => item.slot);
    // One step of a set is booked and its confirmation is in the reply; the
    // steps still listed must not overwrite it with a fresh offer.
    if (stepsLeft) return;
    // An empty offer writes nothing, not a bare header line into the reply.
    const nextDraft = slots.length ? buildSlotsDraftMessage(slots) : '';
    if (nextDraft === responseRef.current) return;
    // Adding or removing a slot used to overwrite the box outright, so a note
    // somebody had typed to a patient vanished with no warning and no undo.
    // Their words stay; the button below folds the times in when they choose.
    if (!canReplaceDraft(responseRef.current, lastGeneratedDraftRef.current)) {
      revealAfterSlotWriteRef.current = null;
      return;
    }
    lastGeneratedDraftRef.current = nextDraft || null;
    setResponse(nextDraft);
    setAiSchedulingError(null);
    // On a phone the reply box is above the picker, scrolled out of view. Say
    // where the text went — but only when it actually went.
    const reveal = revealAfterSlotWriteRef.current;
    revealAfterSlotWriteRef.current = null;
    if (reveal && nextDraft) revealReply(reveal);
  }, [
    aiSchedulingOverrideSlots,
    aiSchedulingOverrideTouched,
    isConfirmationDraft,
    buildSlotsDraftMessage,
    stepsLeft,
  ]);

  // The times are in the list but not in the words the patient would receive.
  // Declining to overwrite silently would be its own trap, so the panel says so.
  const draftMissingSlots = useMemo(() => {
    if (isConfirmationDraft || aiSchedulingOverrideSlots.length === 0 || stepsLeft) return false;
    // Right after a send the box is empty and the offer is untouched; nagging
    // then would be nagging about nothing.
    if (!response.trim() && !aiSchedulingOverrideTouched) return false;
    const generated = buildSlotsDraftMessage(aiSchedulingOverrideSlots.map((item) => item.slot));
    return response.trim() !== generated.trim();
  }, [isConfirmationDraft, aiSchedulingOverrideSlots, aiSchedulingOverrideTouched, response, buildSlotsDraftMessage, stepsLeft]);

  // Where the one Send button goes, and what it carries. Decided in sendPath.ts
  // so it can be read and tested on its own.
  const plannedSend = useMemo(
    () => planSend({
      pendingType: aiScheduling?.pendingType,
      status: schedulingStatus,
      slots: aiSchedulingOverrideSlots.map((item) => item.slot),
    }),
    [aiScheduling?.pendingType, schedulingStatus, aiSchedulingOverrideSlots],
  );

  useEffect(() => {
    if (!open || !displayTask?.id) {
      setCancellation(null);
      setCancellationDismissed(false);
      setChosenAppointment('');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await triageApi.getCancellation(displayTask.id);
        if (!cancelled) setCancellation(res.data?.data || null);
      } catch (err) {
        // A task that cannot be read for cancellation intent simply does not
        // show the panel; it is not worth an error in front of the user.
        if (!cancelled) setCancellation(null);
      }
    })();
    return () => { cancelled = true; };
  }, [open, displayTask?.id]);

  // Keep an open task current without the user refreshing. Every reason not to
  // ask lives in shouldPoll; this only wires it to a timer.
  // Read through a ref rather than a dependency: putting the busy flags in the
  // dependency array tore the timer down and rebuilt it every time one flipped,
  // so the interval was never really 15 seconds.
  const pollBusyRef = useRef(false);
  pollBusyRef.current = isLoading || aiSchedulingLoading || isSending;
  responseRef.current = response;

  useEffect(() => {
    if (!open || !taskId) return;
    const tick = async () => {
      if (!shouldPoll({ isOpen: true, hidden: document.hidden, busy: pollBusyRef.current })) return;
      try {
        const fresh = await fetchTaskDetails();
        if (!fresh) return;
        setTaskDetails((prev: any) => {
          const changed = describeTaskChange(prev, fresh);
          if (changed) setLiveChange(changed);
          return fresh;
        });
      } catch {
        // Silent on purpose. A blip should not throw an error at somebody
        // mid-reply; the next tick tries again.
      }
    };
    const id = setInterval(tick, TASK_POLL_MS);
    return () => clearInterval(id);
  }, [open, taskId]);

  // A link that is out for this patient must be visible when the task is
  // reopened — especially one holding times, which is blocking the diary.
  //
  // The server answers a `live` object shaped exactly like the one a fresh
  // mint returns, so the block reads the same whether the link was made a
  // second ago or a week ago: the same URL, the same expiry, and the actual
  // times it holds rather than a count of them.
  //
  // Authoritative on purpose. It used to only ever set, never clear, so a
  // stale link outlived the task it belonged to; the server's answer is now
  // the whole truth, including "nothing is out".
  useEffect(() => {
    if (!open || !taskId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await schedulingLinksApi.forTask(String(taskId));
        const live = res.data?.live ?? null;
        if (cancelled) return;
        // Every link ever made for the task comes back, newest first. If the
        // newest ran out unused, the panel says so instead of looking as if
        // no link had been sent.
        setLapsedLink(live ? null : lapsedLinkNote(res.data?.data, new Date(), getPracticeTimeZone()));
        setBookingLink(live ? {
          id: live.id,
          url: live.url || '',
          expiresAt: live.expiresAt,
          ttlDays: live.ttlDays,
          createdAt: live.createdAt,
          heldSlots: live.heldSlots || [],
        } : null);
      } catch {
        // Leave whatever is on screen rather than claiming there is no link:
        // a blip must not make a live hold look released.
      }
    })();
    return () => { cancelled = true; };
  }, [open, taskId]);

  // Ask the server what the chosen times would leave of each day, as the
  // office picks them. Debounced: the list changes one tap at a time. The
  // engine is the only honest source — buffer, hours and other holds — so
  // this is never worked out in the browser.
  useEffect(() => {
    const mapping = selectedMapping;
    const slots = aiSchedulingOverrideSlots.map((item: any) => item.slot);
    // One question per diary: a lab draw is held in the nurse's, the visit
    // in the doctor's.
    const groups = previewGroups(slots, mapping).filter((g) => g.serviceId && g.specialistId);
    if (groups.length === 0 || bookingLink) {
      setHoldImpact({ level: 'none', message: null });
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const answers = await Promise.all(groups.map((g) => schedulingLinksApi.preview({
          serviceId: g.serviceId as string,
          specialistId: g.specialistId as string,
          slots: g.slots,
        })));
        // With more than one diary involved, each row says whose it is.
        const rows = answers.flatMap((res, i) => {
          const who = groups.length > 1
            ? slots.find((slot: any) => (slot.specialistId || mapping?.specialist?.id) === groups[i].specialistId)?.specialist
            : null;
          return (res.data?.data || []).map((row: any) => ({ ...row, who }));
        });
        if (!cancelled) setHoldImpact(describeHoldImpact(rows));
      } catch {
        // No preview is a missing warning, not a wrong one.
        if (!cancelled) setHoldImpact({ level: 'none', message: null });
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [aiSchedulingOverrideSlots, selectedMapping, bookingLink]);

  // Creating or withdrawing a link changes what this patient's grid may show,
  // so reload it — but only when the link actually changes, not on every poll.
  useEffect(() => {
    const id = bookingLink?.id || null;
    if (bookingLinkIdRef.current === id) return;
    bookingLinkIdRef.current = id;
    setSlotReloadKey((key) => key + 1);
  }, [bookingLink?.id]);

  // Does this task have a time the patient booked and nobody has confirmed?
  useEffect(() => {
    const appointmentId = aiScheduling?.appointmentId;
    if (!appointmentId) { setTentativeBooking(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await appointmentsApi.getAppointment(String(appointmentId));
        const appt = res.data?.data || res.data?.appointment || res.data;
        if (cancelled) return;
        // Only tentative. A confirmed or cancelled appointment is somebody
        // else's story and does not belong in this panel.
        // Mongo documents carry _id, Postgres rows carry id, and this endpoint
        // can answer with either depending on which store held the appointment.
        // Reading only `id` sent the confirm call to /appointments/undefined.
        const apptId = appt?.id || appt?._id;
        setTentativeBooking(
          appt && apptId && String(appt.status) === 'tentative'
            ? { id: String(apptId), startTime: appt.startTime, provider: appt.provider || appt.specialist || '' }
            : null,
        );
      } catch {
        if (!cancelled) setTentativeBooking(null);
      }
    })();
    return () => { cancelled = true; };
  }, [aiScheduling?.appointmentId, aiScheduling?.updatedAt]);

  // Reset the opt-in every time, so a tick never carries over to a patient who
  // was never meant to get a message. Same rule as the cancellation dialog.
  useEffect(() => { setTellPatientOnConfirm(false); }, [tentativeBooking?.id]);

  if (!displayTask) return null;
  if (!task) return null;

  // Handle appointment save from modal
  const handleAppointmentSave = (appointment: Appointment) => {
    setShowAppointmentModal(false);
    // Booking from a task previously closed the modal with no confirmation at
    // all, so staff had no signal it landed and would rebook. Name the patient
    // and the slot so the details can be checked, not just that "something
    // happened". (Save failures are surfaced inside AppointmentModal itself.)
    try {
      const when = appointment?.startTime;
      const detail = when
        ? `${formatDateOnly(when, dateTimeFormat)} at ${formatTimeOnly(when, dateTimeFormat, false)}`
        : '';
      setPatientToast(
        `Appointment scheduled for ${appointment?.patientName || 'patient'}${detail ? ` on ${detail}` : ''}`,
      );
    } catch {
      setPatientToast('Appointment scheduled.');
    }
  };

  // Some per-message/legacy AI values were stored as the raw JSON blob from the
  // JSON-forced Chatbase agent. Pull out just the prose so the Send Response box
  // never shows JSON, whether the value is plain text or a JSON object.
  const extractAiProse = (raw: any): string => {
    if (typeof raw !== 'string') return '';
    let text = raw.trim();
    const fenced = text.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/i);
    if (fenced) text = fenced[1].trim();
    if (text.startsWith('{')) {
      try {
        const obj = JSON.parse(text);
        const prose = obj?.aiSuggestedResponse || obj?.suggestedResponse || obj?.response;
        if (typeof prose === 'string' && prose.trim()) return prose.trim();
      } catch {
        // not valid JSON - fall through and use the raw text
      }
    }
    return raw.trim();
  };

  const handleUseAISuggestion = () => {
    // Prefer the freshest reply: the latest inbound patient message's AI suggestion
    // (regenerated per incoming message), then fall back to the task-level analysis.
    const msgs: any[] = (displayTask as any)?.conversationMessages || [];
    const latestInbound = [...msgs]
      .reverse()
      .find((m) => m?.direction === 'inbound' && (m?.aiSuggestedResponse || m?.aiAnalysis?.suggestedResponse));
    const candidate =
      latestInbound?.aiSuggestedResponse ||
      latestInbound?.aiAnalysis?.suggestedResponse ||
      (displayTask as any)?.aiSuggestedResponse ||
      (displayTask as any)?.aiAnalysis?.suggestedResponse ||
      '';
    setResponse(extractAiProse(candidate));
  };

  const createResponseId = () =>
    `resp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    const currentResponseId = responseId || createResponseId();
    if (!responseId) {
      setResponseId(currentResponseId);
    }

    setIsUploadingAttachment(true);
    try {
      for (const file of files) {
        const uploadResponse = await triageApi.uploadResponseAttachment(task.id, currentResponseId, file);
        const payload = uploadResponse.data?.data || uploadResponse.data;
        if (payload?.downloadUrl) {
          setPendingAttachments((prev) => [
            ...prev,
            {
              filename: payload.filename || file.name,
              size: payload.size || file.size,
              contentType: file.type,
              downloadUrl: payload.downloadUrl,
              downloadToken: payload.downloadToken,
              source: 'response',
            },
          ]);
        }
      }
    } catch (err) {
      console.error('Failed to upload attachment:', err);
    } finally {
      setIsUploadingAttachment(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const isTextAttachment = (attachment: MessageAttachment) => {
    const filename = attachment.filename?.toLowerCase() || '';
    return attachment.contentType?.startsWith('text/') ||
      filename.endsWith('.txt') ||
      filename.endsWith('.md') ||
      filename.endsWith('.csv') ||
      filename.endsWith('.log') ||
      filename.endsWith('.json');
  };

  const isSpruceAttachment = (attachment: MessageAttachment, message: any) => {
    if (!message?.spruceMessageId || !attachment?.id) {
      return false;
    }
    if (attachment.source === 'response') {
      return false;
    }
    if (attachment.source === 'spruce') {
      return true;
    }
    if (attachment.source) {
      return false;
    }
    return true;
  };

  const getSpruceConversationId = (message?: any) =>
    message?.spruceConversationId ||
    message?.spruceThreadId ||
    (labTask as Task | null)?.spruceThreadId ||
    (labTask as Task | null)?.conversationId;

  const handlePreviewAttachment = async (attachment: MessageAttachment, message: any) => {
    setAttachmentPreviewText("");
    setIsPreviewLoading(true);
    setAttachmentPreviewSource({ attachment, message });
    let previewUrl = attachment.downloadUrl || attachment.url || "";
    let contentType = attachment.contentType || "application/octet-stream";
    let filename = attachment.filename || "Attachment";
    let transcription = attachment.transcription;
    let isObjectUrl = false;
    let didSetPreview = false;

    try {
      const spruceMessageId = message?.spruceMessageId;
      const spruceAttachmentId = attachment.id;
      const spruceConversationId = getSpruceConversationId(message);

      if (isSpruceAttachment(attachment, message) && spruceMessageId && spruceAttachmentId) {
        try {
          const metaResponse = await triageApi.getSpruceAttachment(spruceMessageId, spruceAttachmentId, spruceConversationId);
          const meta = metaResponse.data?.attachment;
          if (meta) {
            contentType = meta.contentType || contentType;
            filename = meta.filename || filename;
            transcription = meta.transcription || transcription;
          }
        } catch (metaError) {
          // Ignore metadata fetch failures; download will still run.
        }

        const downloadResponse = await triageApi.downloadSpruceAttachment(spruceMessageId, spruceAttachmentId, spruceConversationId);
        const blob = downloadResponse.data;
        previewUrl = URL.createObjectURL(blob);
        isObjectUrl = true;
        contentType = blob.type || contentType;
        if (isTextAttachment({ ...attachment, contentType, filename })) {
          const text = await blob.text();
          setAttachmentPreviewText(text);
        }
      } else if (previewUrl) {
        if (isTextAttachment({ ...attachment, contentType, filename })) {
          const textResponse = await fetch(previewUrl);
          const text = await textResponse.text();
          setAttachmentPreviewText(text);
        } else if (attachment.source === "response" || previewUrl.includes("/api/response-attachments/download/")) {
          const blobResponse = await fetch(previewUrl);
          const blob = await blobResponse.blob();
          previewUrl = URL.createObjectURL(blob);
          isObjectUrl = true;
          contentType = blob.type || contentType;
        }
      }

      if (previewUrl) {
        setAttachmentPreview({
          url: previewUrl,
          filename,
          contentType,
          transcription,
          isObjectUrl,
        });
        didSetPreview = true;
      }
    } catch (err) {
      console.error("Failed to preview attachment:", err);
    } finally {
      if (!didSetPreview && previewUrl) {
        setAttachmentPreview({
          url: previewUrl,
          filename,
          contentType,
          transcription,
          isObjectUrl: false,
        });
      }
      setIsPreviewLoading(false);
    }
  };

  const handleDownloadAttachment = async (attachment: MessageAttachment, message: any) => {
    const downloadFallback = () => {
      const downloadUrl = attachment.downloadUrl || attachment.url;
      if (!downloadUrl) {
        return;
      }
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = attachment.filename || "attachment";
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      setTimeout(() => link.remove(), 1000);
    };

    try {
      const spruceMessageId = message?.spruceMessageId;
      const spruceAttachmentId = attachment.id;
      const spruceConversationId = getSpruceConversationId(message);

      if (isSpruceAttachment(attachment, message) && spruceMessageId && spruceAttachmentId) {
        const downloadResponse = await triageApi.downloadSpruceAttachment(spruceMessageId, spruceAttachmentId, spruceConversationId);
        const blob = downloadResponse.data;
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = attachment.filename || "attachment";
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          URL.revokeObjectURL(blobUrl);
          link.remove();
        }, 1000);
        return;
      }
    } catch (err) {
      console.error("Failed to download attachment:", err);
    }

    downloadFallback();
  };

  const handleClosePreview = () => {
    if (attachmentPreview?.isObjectUrl) {
      URL.revokeObjectURL(attachmentPreview.url);
    }
    setAttachmentPreview(null);
    setAttachmentPreviewText('');
    setAttachmentPreviewSource(null);
  };

  // On a phone the reply box sits above the scheduling panel, scrolled out of
  // view. Anything the panel writes into it says so, and brings it on screen.
  const revealReply = (toast: string) => {
    if (isMobile) replyBoxRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setPatientToast(toast);
  };

  // The one Send. It used to be two — this one, which sent text and recorded
  // nothing about scheduling, and the panel's, which sent text and recorded the
  // offered times. /respond now carries the times, so this button does both;
  // only the AI-confirmation branch, which books an appointment, still goes to
  // approve-draft.
  //
  // A failed send keeps everything: the text, the responseId and the pending
  // attachments, so pressing Send again just works. And it says what happened —
  // this used to console.error a bare 500 and show the office nothing.
  const handleSendResponse = async () => {
    const text = response.trim();
    if (!text || !task) return;
    const plan = plannedSend;
    setSendError(null);
    setIsSending(true);
    try {
      if (plan.path === 'approve-draft') {
        await aiSchedulingApi.approveDraft({ taskId: String(taskId), message: text });
      } else {
        await triageApi.sendResponse(task.id, text, responseId || undefined, plan.slots as any[] | undefined);
      }
      setResponse('');
      setResponseId(null);
      setPendingAttachments([]);
      lastGeneratedDraftRef.current = null;
      if (plan.slots) {
        setSlotOverrideSelectedKeys([]);
        setAiSchedulingOverrideTouched(false);
      }
      await refreshTaskDetails();
      setPatientToast(`Sent to ${displayTask?.patientName || 'the patient'}. It is now in the conversation below.`);
    } catch (err: any) {
      setSendError(
        err?.response?.data?.error ||
          err?.message ||
          'The message could not be sent, so nothing was sent. Your message has been kept.',
      );
    } finally {
      setIsSending(false);
    }
  };

  const mergeNoteIntoTask = (updater: (notes: InternalNote[]) => InternalNote[]) => {
    setTaskDetails((prev) => {
      const base = prev || (task as Task | null);
      if (!base) return prev;
      const current = (base.internalNotes || []) as InternalNote[];
      return { ...base, internalNotes: updater(current), hasInternalNotes: true } as Task;
    });
  };

  const handleAddNote = async () => {
    if (!internalNote.trim() || !task) return;
    setIsLoading(true);
    setNoteError(null);
    try {
      const resp = await triageApi.addInternalNote(task.id, internalNote);
      const saved = (resp?.data?.data || resp?.data) as InternalNote | undefined;
      if (saved && saved.id) {
        mergeNoteIntoTask((notes) => [...notes, saved]);
      }
      setInternalNote('');
    } catch (err) {
      console.error('Failed to add note:', err);
      setNoteError('Could not save note. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartNoteEdit = (note: InternalNote) => {
    setEditingNoteId(note.id);
    setNoteDraft(note.text);
    setNoteError(null);
  };

  const handleCancelNoteEdit = () => {
    setEditingNoteId(null);
    setNoteDraft('');
  };

  const handleSaveNoteEdit = async (note: InternalNote) => {
    if (!task) return;
    const trimmed = noteDraft.trim();
    if (!trimmed) return;
    if (trimmed === note.text) { handleCancelNoteEdit(); return; }
    setNoteBusyId(note.id);
    setNoteError(null);
    try {
      const resp = await triageApi.editInternalNote(task.id, note.id, trimmed);
      const updated = (resp?.data?.data || resp?.data) as InternalNote | undefined;
      mergeNoteIntoTask((notes) => notes.map((n) => (n.id === note.id ? (updated || { ...n, text: trimmed }) : n)));
      handleCancelNoteEdit();
    } catch (err: any) {
      console.error('Failed to edit note:', err);
      const msg = err?.response?.data?.error || 'Could not save changes. Please try again.';
      setNoteError(msg);
    } finally {
      setNoteBusyId(null);
    }
  };

  const handleDeleteNote = (note: InternalNote) => setNoteToDelete(note);

  // A recall task is one the system created and wrote all of, so removing one
  // made by mistake destroys nothing a patient said. Completing it instead
  // would claim it was handled.
  const isRecallTask = (displayTask as any)?.source === 'wellness_recall';
  // What the link out there actually holds, and whether the list above still
  // matches it. The offer list stays editable after a link has gone out, so
  // without this the office could read a list of four times while the patient
  // is looking at three.
  const linkHeldSlots = bookingLink?.heldSlots || [];
  // More than one kind of appointment on the link — say which is which.
  const linkHoldsSeveral = new Set(linkHeldSlots.map((s) => `${s.serviceName || ''}|${s.providerName || ''}`)).size > 1;
  const linkDrift = describeLinkDrift(linkHeldSlots, aiSchedulingOverrideSlots.map((item: any) => item.slot));
  // A live link nobody sent holds times for nobody.
  const linkSentWhen = bookingLink?.createdAt
    ? linkSentAt(bookingLink.createdAt, (displayTask as any)?.conversationMessages)
    : null;
  const offerCount = aiSchedulingOverrideSlots.length;
  // The one sentence under the scheduling chip. Every input is a fact the
  // panel already shows somewhere as a caption; see taskNextStep.ts.
  const bookedToldAt = bookedAppointment
    ? confirmationSentAt(bookedAppointment.bookedAt, (displayTask as any)?.conversationMessages)
    : null;
  const nextStep = taskNextStep({
    status: schedulingStatus,
    offerCount,
    link: bookingLink
      ? {
          heldCount: linkHeldSlots.length,
          sentAtLabel: linkSentWhen ? formatTimeOnly(linkSentWhen, dateTimeFormat) : null,
          urlInReply: Boolean(bookingLink.url) && response.includes(bookingLink.url),
        }
      : null,
    booked: bookedAppointment
      ? { label: bookedAppointment.label, toldAtLabel: bookedToldAt ? formatTimeOnly(bookedToldAt, dateTimeFormat) : null }
      : null,
  });

  const deleteThisTask = async () => {
    if (!task) return;
    setDeletingTask(true);
    try {
      await triageApi.deleteTask(task.id);
      setConfirmDeleteTask(false);
      onClose();
      if (onDeleted) onDeleted(String(task.id));
    } catch (err: any) {
      console.error('Failed to delete task:', err);
      setNoteError(err?.response?.data?.error || 'Could not delete this task. Please try again.');
      setConfirmDeleteTask(false);
    } finally {
      setDeletingTask(false);
    }
  };

  const confirmDeleteNote = async () => {
    const note = noteToDelete;
    if (!task || !note) return;
    setNoteBusyId(note.id);
    setNoteError(null);
    try {
      await triageApi.deleteInternalNote(task.id, note.id);
      mergeNoteIntoTask((notes) => notes.filter((n) => n.id !== note.id));
    } catch (err: any) {
      console.error('Failed to delete note:', err);
      const msg = err?.response?.data?.error || 'Could not delete note. Please try again.';
      setNoteError(msg);
    } finally {
      setNoteBusyId(null);
      setNoteToDelete(null);
    }
  };

  const openNoteHistory = (e: React.MouseEvent<HTMLElement>, note: InternalNote) => {
    setHistoryAnchor(e.currentTarget);
    setHistoryNote(note);
  };

  const closeNoteHistory = () => {
    setHistoryAnchor(null);
    setHistoryNote(null);
  };

  const formatRelative = (iso?: string) => {
    if (!iso) return '';
    const ms = Date.now() - new Date(iso).getTime();
    if (Number.isNaN(ms)) return '';
    const s = Math.round(ms / 1000);
    if (s < 60) return 'just now';
    const m = Math.round(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.round(h / 24);
    if (d < 30) return `${d}d ago`;
    return '';
  };

  const handleLabSearch = async () => {
    if (!labSearchQuery.trim()) return;
    setLabSearchLoading(true);
    setLabError(null);
    try {
      const response = await contactsApi.searchContacts(labSearchQuery.trim());
      const data = response.data?.data || response.data || [];
      const results = Array.isArray(data) ? data.map(normalizeContact) : [];
      setLabSearchResults(results);
    } catch (err: any) {
      setLabError(err?.message || 'Failed to search contacts');
    } finally {
      setLabSearchLoading(false);
    }
  };

  const handleSearchAndAttachLabFax = async (summarize: boolean) => {
    if (!labSearchQuery.trim() || !selectedLabFaxId) return;
    setLabSearchLoading(true);
    setLabError(null);
    try {
      const response = await contactsApi.searchContacts(labSearchQuery.trim());
      const data = response.data?.data || response.data || [];
      const results = Array.isArray(data) ? data.map(normalizeContact) : [];
      setLabSearchResults(results);

      if (results.length === 1) {
        setLabSearchLoading(false);
        await handleAttachLabFax(results[0].id, summarize);
        return;
      }

      if (results.length === 0) {
        setLabError('No matching patients found.');
      } else {
        setLabError('Multiple matches found. Select a patient below.');
      }
    } catch (err: any) {
      setLabError(err?.message || 'Failed to search contacts');
    } finally {
      setLabSearchLoading(false);
    }
  };

  const handleAttachLabFax = async (contactId: string, summarize: boolean) => {
    if (!selectedLabFaxId) return;
    setLabAttachLoading(true);
    setLabError(null);
    try {
      await unfiledLabsApi.attach(selectedLabFaxId, { contactId, summarize });
      await refreshTaskDetails();
      setLabSearchResults([]);
      setLabSearchQuery('');
      setLabAttachToast('Saved to Patient Details');
    } catch (err: any) {
      setLabError(err?.message || 'Failed to attach lab fax');
    } finally {
      setLabAttachLoading(false);
    }
  };

  const handleConfirmBooking = async () => {
    if (!tentativeBooking) return;
    setConfirmingBooking(true);
    try {
      const res = await schedulingLinksApi.confirmBooking(tentativeBooking.id);
      const data = res.data?.data || res.data || {};
      if (tellPatientOnConfirm) {
        // Into the reply box, not out of the door. Pressing Send is what
        // actually reaches the patient and what logs it in the conversation.
        const message = bookingConfirmation({
          patientName: data.patientName || displayTask?.patientName || '',
          when: practiceDateTimeLabel(data.startTime || tentativeBooking.startTime),
          providerName: data.providerName || tentativeBooking.provider || undefined,
        });
        setResponse((prev) => (prev.trim() ? `${prev.trim()}\n\n${message}` : message));
        setPatientToast('Confirmed. The message is in the reply box — press Send to tell them.');
      } else {
        setPatientToast('Confirmed. The patient has not been told.');
      }
      setTentativeBooking(null);
      await refreshTaskDetails();
    } catch (err: any) {
      setAiSchedulingError(
        err?.response?.data?.error || 'The booking could not be confirmed. Try again.',
      );
    } finally {
      setConfirmingBooking(false);
    }
  };


  const handleToggleManualSlot = (slotKey: string) => {
    setSlotOverrideSelectedKeys((prev) =>
      prev.includes(slotKey) ? prev.filter((key) => key !== slotKey) : [...prev, slotKey]
    );
  };

  const handleSelectAllManualSlots = () => {
    setSlotOverrideSelectedKeys(manualSlotOptions.map((option) => option.key));
  };

  const handleClearManualSlots = () => {
    setSlotOverrideSelectedKeys([]);
  };

  // Offer a whole appointment rule at once — the labs and the visit for a
  // wellness exam, or the one-morning pre-op — rather than making staff pick
  // each part and remember the gap between them.
  const handleProposeBundle = async (bundleKey: string, from: string = slotOverrideDate) => {
    if (proposingBundle) return;
    setSlotOverrideError(null);
    setProposingBundle(bundleKey);
    try {
      const res = await schedulingApi.getBundleProposal(bundleKey, from);
      const steps = res.data?.data || [];
      if (!steps.length) {
        setSlotOverrideError('No times available for that appointment type.');
        return;
      }
      setLastProposals((prev) => ({ ...prev, [bundleKey]: steps.map((step: any) => ({ date: String(step.date || '') })) }));
      setAiSchedulingOverrideSlots((prev) => {
        const existingKeys = new Set(prev.map((item) => item.key));
        const merged = [...prev];
        steps.forEach((step: any) => {
          const key = `bundle_${bundleKey}_${step.stepOrder}_${step.date}_${step.time}`;
          if (existingKeys.has(key)) return;
          merged.push({
            key,
            slot: {
              date: step.date,
              time: step.time,
              specialist: step.specialistName,
              specialistId: step.specialistId,
              serviceType: step.serviceName,
              serviceId: step.serviceId,
              duration: step.duration,
              startTime: step.startTime,
            },
          });
          existingKeys.add(key);
        });
        return merged;
      });
      setAiSchedulingOverrideTouched(true);
    } catch (err: any) {
      // A 409 means the rule could not be satisfied — say so rather than
      // silently proposing a partial set.
      setSlotOverrideError(
        err?.response?.data?.error || 'Could not build a proposal for that appointment type.'
      );
    } finally {
      setProposingBundle(null);
    }
  };

  const handleCancelAppointment = async () => {
    const appointment = cancellation?.upcomingAppointments.find((a) => a.id === chosenAppointment);
    if (!appointment) return;
    setCancelBusy(true);
    try {
      const res = await schedulingApi.cancelAppointment(appointment.id);
      const sent = Boolean(res?.data?.data?.patientText?.sent);
      // The cancel text already went out. Leave the reply box alone so the
      // same sentence is not sent a second time. If it did not go, start the
      // reply the office can send themselves.
      if (!sent) {
        const startsAt = new Date(appointment.startTime);
        const when = `${formatDateOnly(startsAt, dateTimeFormat)} at ${formatTimeOnly(startsAt, dateTimeFormat, false)}`;
        setResponse((prev) => prev || `Your appointment on ${when} has been cancelled. Would you like to rebook?`);
      }
      setCancellation((prev) => prev && ({
        ...prev,
        upcomingAppointments: prev.upcomingAppointments.filter((a) => a.id !== appointment.id),
      }));
      setChosenAppointment('');
    } catch (err) {
      console.error('Failed to cancel the appointment:', err);
    } finally {
      setCancelBusy(false);
    }
  };

  // Adds a typed time to the offer — if the calendar says it is free. The
  // server judges it by the same rule the list above is built from, so it is
  // held and booked exactly like a listed time; a time that clashes, falls
  // outside the provider's hours or has passed is refused, with the reason.
  const handleAddTypedTime = async () => {
    if (typedTimeBusy) return;
    const mapping = selectedMapping;
    if (!mapping) { setTypedTimeNote('Choose a service and provider first.'); return; }
    const time = clockFromParts(pickedHour, pickedMinute, pickedPeriod);
    const label = formatClock(time);
    const date = slotOverrideDate;
    if (alreadyOffered(aiSchedulingOverrideSlots, { date, time, specialistId: mapping.specialist.id })) {
      setTypedTimeNote(`${label} is already in the offer.`);
      return;
    }
    setTypedTimeBusy(true);
    setTypedTimeNote(null);
    try {
      const res = await schedulingApi.checkTime(
        mapping.service.id, mapping.specialist.id, date, time, bookingLinkIdRef.current || undefined,
      );
      const verdict = res.data?.data || {};
      if (!verdict.free) {
        setTypedTimeNote(typedTimeRefusal(label, verdict.reason, mapping.specialist.name));
        return;
      }
      const entry = typedSlot(date, time, Number(verdict.duration) || 30, mapping);
      setAiSchedulingOverrideSlots((prev) =>
        (alreadyOffered(prev, entry.slot)
          ? prev
          // In the order the times happen: a time picked here is often
          // earlier than one already in the offer.
          : inTimeOrder([...prev, { key: entry.key, slot: entry.slot as SchedulingSlot }])));
      revealAfterSlotWriteRef.current = 'Time added to your reply';
      setAiSchedulingOverrideTouched(true);
      // The lists stay where they are: the next time is usually a nudge of
      // the minutes from this one.
    } catch (err: any) {
      setTypedTimeNote(err?.response?.status === 429
        ? 'Too many requests just now. Wait a moment and try again.'
        : 'Could not check that time. Please try again.');
    } finally {
      setTypedTimeBusy(false);
    }
  };

  const handleAddManualSlots = () => {
    const selectedOptions = manualSlotOptions.filter((option) => slotOverrideSelectedKeys.includes(option.key));
    if (!selectedOptions.length) {
      setSlotOverrideError('Select at least one slot to add.');
      return;
    }
    setAiSchedulingOverrideSlots((prev) => {
      const existingKeys = new Set(prev.map((item) => item.key));
      const merged = [...prev];
      selectedOptions.forEach((option) => {
        if (!existingKeys.has(option.key)) {
          merged.push({ key: option.key, slot: option.slot });
          existingKeys.add(option.key);
        }
      });
      return inTimeOrder(merged);
    });
    revealAfterSlotWriteRef.current = 'Times added to your reply';
    setAiSchedulingOverrideTouched(true);
    setSlotOverrideSelectedKeys([]);
    setSlotOverrideError(null);
  };

  const handleUseOverrideSlots = () => {
    if (!aiSchedulingOverrideSlots.length) {
      setAiSchedulingError('Add at least one slot before updating the draft.');
      return;
    }
    const nextDraft = buildSlotsDraftMessage(aiSchedulingOverrideSlots.map((item) => item.slot));
    if (nextDraft) {
      lastGeneratedDraftRef.current = nextDraft;
      setResponse(nextDraft);
      setAiSchedulingOverrideTouched(true);
      setAiSchedulingError(null);
      revealReply('Times added to your reply');
    }
  };

  // The message half of this screen and the calendar half never met: staff
  // could send "let's do tomorrow at noon" with no appointment existing
  // anywhere, which reads as done and is not. When the patient has already said
  // any time works, picking one time and booking it is the whole job.
  //
  // Books through the same endpoint the calendar's own form uses, so there is
  // one create path with one conflict guard rather than two to keep in step.
  // It sends nothing — a person still presses Send Response.
  const handleBookSlot = async (slotKey: string) => {
    const chosen = aiSchedulingOverrideSlots.find((item) => item.key === slotKey);
    if (!chosen) return;
    const slot = chosen.slot;
    // The time's own service and provider — a lab draw in a set is booked as
    // a lab draw with the nurse whatever the dropdown shows. A time that
    // names neither (an older saved offer) takes the dropdown's, as before.
    const own = slot.serviceId
      ? slotOverrideMappings.find((m) => m.service?.id === slot.serviceId && m.specialist?.id === slot.specialistId) || null
      : null;
    const mapping = own || selectedMapping;
    if (!mapping) {
      setAiSchedulingError('Choose a service and provider before booking.');
      return;
    }
    const startIso = slot.startTime || (slot.date && slot.time ? `${slot.date}T${String(slot.time).slice(0, 5)}:00` : '');
    const start = startIso ? new Date(startIso) : null;
    if (!start || Number.isNaN(start.getTime())) {
      setAiSchedulingError('That slot has no usable time.');
      return;
    }
    const minutes = slot.duration || mapping.service?.duration || 30;
    setBookingSlotKey(slotKey);
    setAiSchedulingError(null);
    // Booking a new time for a patient who is already booked does not cancel
    // the first appointment — it may be a move, or a second visit. Read the
    // earlier one now, so the "Booked" box can say it is still there.
    let earlier: string | null = null;
    if (aiScheduling?.status === 'appointment_confirmed' && aiScheduling?.appointmentId) {
      try {
        const prior = await appointmentsApi.getAppointment(String(aiScheduling.appointmentId));
        const data = prior.data?.appointment || prior.data?.data || prior.data || {};
        const stands = data.startTime && !['cancelled', 'no_show'].includes(String(data.status || '').toLowerCase());
        if (stands) earlier = practiceDateTimeLabel(data.startTime);
      } catch {
        earlier = null;
      }
    }
    try {
      const res = await schedulingApi.createAppointment({
        title: `${mapping.service?.name || 'Appointment'} - ${displayTask?.patientName || 'Patient'}`,
        patientName: displayTask?.patientName || 'Patient',
        patientPhone: (displayTask as any)?.patientPhone || '',
        startTime: start.toISOString(),
        endTime: new Date(start.getTime() + minutes * 60 * 1000).toISOString(),
        appointmentType: String(mapping.service?.name || 'appointment').toLowerCase().replace(/\s+/g, '-'),
        duration: minutes,
        status: 'scheduled',
        provider: mapping.specialist?.name || '',
        specialistId: mapping.specialist?.id || '',
        serviceType: mapping.service?.name || '',
        // So the task hears about it — the thread entry and the appointment
        // id, as a link booking writes — and a later cancellation reaches it.
        taskId: String(taskId),
      });
      const created = res.data?.appointment || res.data?.data || res.data;
      const label = formatSlotLabel(slot, dateTimeFormat);
      setBookedAppointment({ id: created?.id || '', label, bookedAt: new Date().toISOString(), earlier });

      // The slot has stopped being a time we are offering and become an
      // appointment, so it leaves the draft list. Not cosmetic: the effect that
      // keeps the box in step with the picked slots would otherwise put the
      // numbered list straight back over the confirmation written below —
      // the list is machine-written too, so the guard cannot tell them apart.
      // The other steps of a set stay listed, to be booked next; a plain offer
      // is over. Only steps of a different kind count as "left".
      const remaining = aiSchedulingOverrideSlots.filter((item) =>
        (item.slot.serviceId || '') !== (slot.serviceId || '') || (item.slot.specialistId || '') !== (slot.specialistId || ''));
      const keepRest = offerGroups.length > 1 && remaining.length > 0;
      setAiSchedulingOverrideSlots(keepRest ? remaining : []);
      setAiSchedulingOverrideTouched(keepRest);
      setStepsLeft(keepRest);
      setSlotOverrideSelectedKeys([]);
      setSlotReloadKey((n) => n + 1);

      // The office booked by hand, so any link still out for this patient
      // would let them book a second time on top of it. Withdraw it; its held
      // times go back on offer.
      if (bookingLink?.id) {
        try { await schedulingLinksApi.revoke(bookingLink.id); } catch { /* best effort */ }
        setBookingLink(null);
      }

      // Confirmation wording, written the same way the slot list is — through
      // the ref — so it cannot overwrite something a person typed.
      const bookedNow: BookedForReply[] = [...bookedThisSession, {
        serviceName: mapping.service?.name || '', providerName: mapping.specialist?.name || '', date: slot.date, time: slot.time,
      }];
      setBookedThisSession(bookedNow);
      const confirmation = bookedReplyText(displayTask?.patientName || '', bookedNow);
      if (canReplaceDraft(response, lastGeneratedDraftRef.current)) {
        lastGeneratedDraftRef.current = confirmation;
        setResponse(confirmation);
        revealReply('Confirmation added to your reply');
      }
      await refreshTaskDetails();
    } catch (err: any) {
      const status = err?.response?.status;
      setAiSchedulingError(
        status === 409
          ? 'That time was taken while this was open. Pick another.'
          : err?.response?.data?.error || err?.message || 'Could not book that time.',
      );
    } finally {
      setBookingSlotKey(null);
    }
  };

  // Hand the patient a link and let them choose. The office picks the service
  // and provider — the link only decides WHEN — and what comes back is a
  // tentative appointment plus a task, never a confirmed booking.
  //
  // Copied rather than sent: OUTBOUND_ENABLED is unset, so the app cannot text
  // it yet. Saying that on screen beats a Send button that silently fails.
  // The practice may have written its own offer wording in Admin > Settings.
  // Blank, or unreadable, means the standard one — a link that exists must
  // not be left without words because a setting failed to load.
  const readOfferTemplate = async (): Promise<string> => {
    try {
      const setting = await settingsApi.get('scheduling_offer_message');
      return String(setting.data?.setting?.value || '');
    } catch (err) {
      return '';
    }
  };

  // Puts the link's message back into the reply — after reopening a task the
  // box is empty and the link is still out. A link that holds times gets the
  // held-times wording (what is held, for which appointment, by when); only
  // an open link gets the open-calendar invitation. It used to write the
  // invitation for both, which told a patient to "choose a time" on a link
  // that was holding two for them. Added to what is there, never over it.
  const handleAddLinkToReply = async () => {
    if (!bookingLink) return;
    const held = bookingLink.heldSlots || [];
    if (held.length === 0) {
      setResponse((prev) => withInvite(prev, bookingLink.url, displayTask?.patientName || '', {
        service: selectedMapping?.service?.name, provider: selectedMapping?.specialist?.name, expiresAt: bookingLink.expiresAt,
      }));
      revealReply('Link added to your reply');
      return;
    }
    const message = heldOfferMessage({
      patientName: displayTask?.patientName || '',
      slots: held.map((h) => ({ date: h.date, time: h.time, specialist: h.providerName, serviceType: h.serviceName })),
      url: bookingLink.url,
      expiresAt: bookingLink.expiresAt,
      template: await readOfferTemplate(),
    });
    setResponse((prev) => {
      const existing = String(prev || '').trim();
      if (!existing) return message;
      if (existing.includes(bookingLink.url)) return existing;
      return `${existing}\n\n${message}`;
    });
    revealReply('The offer is in your reply');
  };

  const handleCreateBookingLink = async (opts: { purpose?: 'wellness'; openEnded?: boolean } = {}) => {
    const mapping = selectedMapping;
    if (!mapping) { setAiSchedulingError('Choose a service and provider first.'); return; }
    const phone = (displayTask as any)?.patientPhone;
    if (!phone) { setAiSchedulingError('This task has no phone number, so a link cannot be created.'); return; }
    // The times in the offer list go with the link and are HELD: from this
    // moment nobody else is offered them. Opening the calendar to the patient
    // is now a separate, deliberate action rather than what happens when this
    // list is empty — an empty list used to mint an open link silently, so
    // pressing the button while the slots were still loading handed the
    // patient the whole diary while the office believed times were reserved.
    const offered = opts.openEnded ? [] : aiSchedulingOverrideSlots.map((item) => item.slot);
    if (!opts.openEnded && offered.length === 0) {
      setAiSchedulingError('Choose the times you want to hold first — or use "Let them pick any free time" to open the calendar to them.');
      return;
    }
    // Each time is held for its own service and provider, not for whatever
    // the dropdown happens to show: the Wellness exam button puts a lab draw
    // and a doctor's visit in one offer, and holding both in one diary
    // booked only one of them (offerAppointments).
    const requested = linkRequest(offered, mapping);
    if (!requested.serviceId || !requested.specialistId) { setAiSchedulingError('Choose a service and provider first.'); return; }
    setBookingLinkBusy(true);
    setAiSchedulingError(null);
    try {
      const res = await schedulingLinksApi.create({
        taskId: String(taskId),
        patientName: displayTask?.patientName || 'Patient',
        patientPhone: phone,
        internalPatientId: (displayTask as any)?.internalPatientId || undefined,
        serviceId: requested.serviceId,
        specialistId: requested.specialistId,
        slots: requested.slots,
        // Outreach lives longer than a reply: the setting decides how long.
        purpose: opts.purpose,
      });
      const created = res.data?.data || res.data;
      // The response says what was held; the offer says what each is called.
      const heldSlots = (created.heldSlots || []).map((h: any) => {
        const from = offered.find((o: any) => o.date === h.date && String(o.time || '').slice(0, 5) === h.time);
        return { ...h, serviceName: from?.serviceType, providerName: from?.specialist };
      });
      setLapsedLink(null);
      setBookingLink({
        id: created.id, url: created.url, expiresAt: created.expiresAt,
        ttlDays: created.ttlDays, createdAt: new Date().toISOString(), heldSlots,
      });

      if (opts.purpose === 'wellness') {
        // The words are Allison's, from Admin > Settings; the app fills the
        // blanks. {provider} is the doctor the visit is with, even though the
        // link books the lab - the wording explains the two-step itself.
        let template = '';
        try {
          const setting = await settingsApi.get('wellness_outreach_message');
          template = String(setting.data?.setting?.value || '');
        } catch (err) {
          template = '';
        }
        if (!template.trim()) {
          template = STANDARD_OUTREACH_TEMPLATE;
        }
        const doctor = slotOverrideMappings.find((m: any) => /\bdr\.?\b|doctor/i.test(String(m?.specialist?.name || '')));
        const rendered = renderOutreach(template, {
          firstName: displayTask?.patientName || '',
          provider: doctor?.specialist?.name || mapping.specialist?.name || '',
          link: created.url,
          expires: linkLifetimePhrase(created.ttlDays),
        });
        // With held times, they follow the outreach, spaced like any offer.
        const message = heldSlots.length ? `${rendered}\n\n${slotsDraftMessage(offered, dateTimeFormat)}` : rendered;
        lastGeneratedDraftRef.current = message;
        setResponse(message);
        revealReply('Outreach message is in your reply — read it over, then send');
      } else if (heldSlots.length) {
        // One message: the held times, spaced, then a single tap-to-book line.
        // Written through the ref, so it counts as machine-made like the slot
        // list and the guard treats it the same way.
        const offerTemplate = await readOfferTemplate();
        const message = heldOfferMessage({
          patientName: displayTask?.patientName || '',
          slots: offered,
          url: created.url,
          // The deadline the patient is told is the link's own, not a phrase.
          expiresAt: created.expiresAt,
          template: offerTemplate,
        });
        lastGeneratedDraftRef.current = message;
        setResponse(message);
        revealReply(`${heldSlots.length === 1 ? 'That time is' : 'Those times are'} held for the patient — the offer is in your reply`);
      } else {
        // The message box is directly below this button, and sending the link is
        // the entire point of making one. Appended, never substituted: a link is
        // something you add to what you were already saying, unlike the slot list
        // which replaces a list of times with a list of times.
        //
        // lastGeneratedDraftRef is deliberately NOT updated. That leaves the box
        // counting as human-written, so the effect that keeps it in step with the
        // picked slots will not overwrite it — and the link survives somebody
        // adding another time afterwards.
        setResponse((prev) => withInvite(prev, created.url, displayTask?.patientName || '', {
          service: mapping.service?.name, provider: mapping.specialist?.name, expiresAt: created.expiresAt,
        }));
        revealReply('Link added to your reply');
      }

      await copyLinkToClipboard(created.url);
    } catch (err: any) {
      const unheld: Array<{ date: string; time: string }> = err?.response?.data?.unheldSlots || [];
      if (unheld.length) {
        // A time went while this task was open. Drop it from the offer so the
        // next attempt holds what is actually free.
        const gone = new Set(unheld.map((u) => `${u.date} ${u.time}`));
        setAiSchedulingOverrideSlots((prev) =>
          prev.filter((item: any) => !gone.has(`${item.slot.date} ${String(item.slot.time || '').slice(0, 5)}`)),
        );
        setAiSchedulingError(
          `${err.response.data.error} Removed from your offer: ${unheld.map((u) => `${u.date} ${u.time}`).join(', ')}. Create the link again.`,
        );
      } else {
        setAiSchedulingError(err?.response?.data?.error || 'Could not create a booking link.');
      }
    } finally {
      setBookingLinkBusy(false);
    }
  };

  // Explicit, with feedback. A silent auto-copy that the browser refused looks
  // identical to one that worked.
  const copyLinkToClipboard = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      window.setTimeout(() => setLinkCopied(false), 4000);
      return true;
    } catch {
      // Browsers withhold the clipboard when the page is not focused, and some
      // refuse it outright. Selecting the field is the fallback that still lets
      // them copy — better than a button that appears to do nothing.
      setLinkCopied(false);
      bookingLinkFieldRef.current?.focus();
      bookingLinkFieldRef.current?.select();
      return false;
    }
  };

  // Withdraw what is out and mint a fresh link holding whatever the offer list
  // says now. Two steps because they are two facts: the link the patient may
  // already have stops working, and a new one takes its place. Never automatic
  // — only the office knows whether the old one has been sent.
  const handleReplaceBookingLink = async (opts: { openEnded?: boolean } = {}) => {
    if (!bookingLink) return;
    setBookingLinkBusy(true);
    try {
      await schedulingLinksApi.revoke(bookingLink.id);
    } catch {
      // Already gone is the outcome we wanted anyway.
    }
    setBookingLink(null);
    setLinkCopied(false);
    setBookingLinkBusy(false);
    await handleCreateBookingLink(opts);
  };

  const handleRevokeBookingLink = async () => {
    if (!bookingLink) return;
    setBookingLinkBusy(true);
    try {
      await schedulingLinksApi.revoke(bookingLink.id);
      // Said, not done: rewriting somebody's reply under them is worse than
      // telling them it needs a look.
      if (bookingLink.url && responseRef.current.includes(bookingLink.url)) {
        setAiSchedulingError('Withdrawn. Your reply still contains that link — it no longer works, so edit it before sending.');
      }
      setBookingLink(null);
      setLinkCopied(false);
    } catch (err: any) {
      setAiSchedulingError(err?.response?.data?.error || 'Could not withdraw that link.');
    } finally {
      setBookingLinkBusy(false);
    }
  };

  const handleClearOverrideSlots = () => {
    setAiSchedulingOverrideSlots([]);
    setAiSchedulingOverrideTouched(true);
  };

  const handleRemoveOverrideSlot = (slotKey: string) => {
    setAiSchedulingOverrideSlots((prev) => prev.filter((item) => item.key !== slotKey));
    setAiSchedulingOverrideTouched(true);
  };

  // Points the picker above at this step's service, provider and day, so the
  // listed times and "Need a time that is not listed?" add to it — what
  // switching the dropdown by hand did, with nothing to guess.
  const handleAddTimeForStep = (step: OfferStep) => {
    const mapping = slotOverrideMappings.find(
      (m) => m.service?.id === step.serviceId && m.specialist?.id === step.specialistId,
    );
    if (!mapping) {
      setSlotOverrideError(`${step.title} is not in the service list above, so times for it can only be proposed.`);
      return;
    }
    setSlotOverrideError(null);
    setSlotOverrideMappingId(mapping.id);
    if (step.firstDate) setSlotOverrideDate(step.firstDate);
    setSlotOverrideRange('day');
    window.setTimeout(() => pickerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
  };


  // Clears an AI-written draft out of the reply box and tells the backend it
  // was declined, which is what moves the task off "Needs Approval". Clearing
  // the text alone would leave that chip lit forever.
  const handleDiscardAiDraft = async () => {
    if (!taskId) return;
    setAiSchedulingLoading(true);
    setSendError(null);
    setResponse('');
    lastGeneratedDraftRef.current = null;
    try {
      await aiSchedulingApi.rejectDraft({ taskId: String(taskId) });
      await refreshTaskDetails();
    } catch (err: any) {
      setSendError(err?.response?.data?.error || err?.message || 'The draft could not be discarded.');
    } finally {
      setAiSchedulingLoading(false);
    }
  };

  const updateSplitRow = (rowId: string, field: 'startPage' | 'endPage', value: string) => {
    setLabSplitRows((rows) =>
      rows.map((row) => (row.id === rowId ? { ...row, [field]: value } : row))
    );
  };

  const addSplitRow = () => {
    setLabSplitRows((rows) => [...rows, createSplitRow()]);
  };

  const removeSplitRow = (rowId: string) => {
    setLabSplitRows((rows) => (rows.length > 1 ? rows.filter((row) => row.id !== rowId) : rows));
  };

  const handleCreateSplits = async () => {
    if (!selectedLabFaxId) return;
    const totalPages = labFaxDetail?.page_count || selectedLabFax?.page_count || null;
    const normalized = labSplitRows.map((row) => ({
      startPage: Number(row.startPage),
      endPage: Number(row.endPage),
    }));

    if (normalized.some((row) => !Number.isInteger(row.startPage) || !Number.isInteger(row.endPage))) {
      setLabSplitError('Enter a start and end page for each split.');
      return;
    }
    if (normalized.some((row) => row.startPage < 1 || row.endPage < 1 || row.startPage > row.endPage)) {
      setLabSplitError('Each split must have a valid page range.');
      return;
    }
    if (totalPages && normalized.some((row) => row.endPage > totalPages)) {
      setLabSplitError(`Split ranges must be within ${totalPages} pages.`);
      return;
    }

    const sorted = [...normalized].sort((a, b) => a.startPage - b.startPage);
    for (let i = 1; i < sorted.length; i += 1) {
      if (sorted[i].startPage <= sorted[i - 1].endPage) {
        setLabSplitError('Split ranges cannot overlap.');
        return;
      }
    }

    setLabSplitLoading(true);
    setLabSplitError(null);
    try {
      await unfiledLabsApi.split(selectedLabFaxId, { splits: normalized });
      await refreshTaskDetails();
      setSelectedLabFaxId(null);
      setLabSplitRows([createSplitRow()]);
      setShowSplitPanel(false);
    } catch (err: any) {
      setLabSplitError(err?.response?.data?.message || err?.message || 'Failed to split PDF');
    } finally {
      setLabSplitLoading(false);
    }
  };

  // One handler for the three states. Any change closes the task, as the
  // two forward-only buttons always did: "In Progress" has meant "I'm on
  // it, next" to the team since the start.
  const handleSetStatus = async (status: TaskStatusKey) => {
    if (status === task.status) return;
    setIsLoading(true);
    try {
      await triageApi.updateTask(task.id, { status });
      onUpdate({ ...task, status });
      onClose();
    } catch (err) {
      console.error('Failed to change the task status:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCategoryChange = async (category: TaskCategory) => {
    try {
      await triageApi.updateTask(task.id, { category });
      onUpdate({ ...task, category });
    } catch (err) {
      console.error('Failed to update category:', err);
    }
  };

  const handleOpenAssignDialog = async () => {
    setAssignDialogOpen(true);
    setAssignError(null);
    setAssignLoading(true);
    try {
      const response = await triageApi.getAssignableUsers();
      const data = response.data?.data || [];
      setAssignees(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setAssignError(err?.message || 'Failed to load users');
    } finally {
      setAssignLoading(false);
    }
  };

  const handleAssignSave = async () => {
    if (!selectedAssigneeId) {
      setAssignError('Select a user to assign this task.');
      return;
    }
    setAssignLoading(true);
    setAssignError(null);
    try {
      const assignedId = String(selectedAssigneeId);
      await triageApi.assignTask(task.id, assignedId);
      const assignedUser = assignees.find((user) => String(user.id) === assignedId) || undefined;
      const updatedTask: Task = {
        ...(displayTask as Task),
        assignedTo: assignedId,
        assignedToUser: assignedUser ? { id: assignedUser.id, name: assignedUser.name, email: assignedUser.email } : undefined,
      };
      setTaskDetails(updatedTask);
      onUpdate(updatedTask);
      setAssignDialogOpen(false);
    } catch (err: any) {
      setAssignError(err?.response?.data?.error || err?.message || 'Failed to assign task');
    } finally {
      setAssignLoading(false);
    }
  };

  const handleFollowUpSave = async () => {
    if (!followUpDate) {
      setFollowUpError('Select a follow-up date.');
      return;
    }
    setFollowUpLoading(true);
    setFollowUpError(null);
    try {
      await triageApi.updateTask(task.id, { followUpDate });
      const updatedTask: Task = {
        ...(displayTask as Task),
        followUpDate,
      };
      setTaskDetails(updatedTask);
      onUpdate(updatedTask);
      setFollowUpDialogOpen(false);
      setPatientToast('Follow-up date scheduled.');
      onClose();
    } catch (err: any) {
      setFollowUpError(err?.response?.data?.error || err?.message || 'Failed to set follow-up date');
    } finally {
      setFollowUpLoading(false);
    }
  };

  const handleSavePatientName = async () => {
    const fullName = `${patientFirstName} ${patientLastName}`.trim();
    if (!fullName) {
      setPatientSaveError('Enter a patient name.');
      return;
    }
    setPatientSaveLoading(true);
    setPatientSaveError(null);
    try {
      const response = await triageApi.updatePatientInfo(task.id, {
        firstName: patientFirstName,
        lastName: patientLastName,
        fullName,
        patientPhone: task.patientPhone,
        contactId: (displayTask as Task)?.associatedContactIds?.[0],
        spruceContactId: (displayTask as Task)?.spruceContactId,
      });
      const data = response.data?.data || {};
      const updatedTask: Task = {
        ...(displayTask as Task),
        patientName: data.patientName || fullName,
        patientPhone: data.patientPhone || task.patientPhone,
      };
      if (data.contactId) {
        const contactIds = new Set((displayTask as Task)?.associatedContactIds || []);
        contactIds.add(data.contactId);
        updatedTask.associatedContactIds = Array.from(contactIds);
      }
      setTaskDetails(updatedTask);
      onUpdate(updatedTask);
      setIsEditingPatient(false);
      const duplicates = Array.isArray(data.duplicates) ? data.duplicates : [];
      const primaryContactId = data.contactId ? String(data.contactId) : null;
      const candidates: Array<{
        id: string;
        fullName?: string;
        phone?: string;
        isArchived?: boolean;
      }> = duplicates
        .filter((match: any) => String(match.id) !== primaryContactId)
        .map((match: any) => ({
          id: String(match.id),
          fullName: match.fullName || match.full_name || 'Unknown',
          phone: match.phone,
          isArchived: match.isArchived ?? match.is_archived,
        }));

      if (primaryContactId && candidates.length > 0) {
        setMergePrimaryId(primaryContactId);
        setMergeCandidates(candidates);
        setMergeSelection(candidates.map((candidate) => candidate.id));
        setMergeDialogOpen(true);
        setPatientToast(`Found ${candidates.length + 1} matching contacts. Review duplicates to merge.`);
      } else {
        setPatientToast('Patient name updated.');
      }
    } catch (err: any) {
      setPatientSaveError(err?.response?.data?.error || err?.message || 'Failed to update patient name');
    } finally {
      setPatientSaveLoading(false);
    }
  };

  const handleToggleMergeSelection = (contactId: string) => {
    setMergeSelection((prev) =>
      prev.includes(contactId) ? prev.filter((id) => id !== contactId) : [...prev, contactId]
    );
  };

  const handleCloseMergeDialog = () => {
    if (mergeLoading) return;
    setMergeDialogOpen(false);
    setMergeCandidates([]);
    setMergeSelection([]);
    setMergePrimaryId(null);
    setMergeError(null);
  };

  const handleMergeContacts = async () => {
    if (!mergePrimaryId) {
      setMergeError('Primary contact missing.');
      return;
    }
    if (mergeSelection.length === 0) {
      setMergeError('Select at least one contact to merge.');
      return;
    }
    setMergeLoading(true);
    setMergeError(null);
    const mergedCount = mergeSelection.length;
    try {
      await contactsApi.mergeContacts(mergePrimaryId, mergeSelection);
      setMergeDialogOpen(false);
      setMergeCandidates([]);
      setMergeSelection([]);
      setMergePrimaryId(null);
      setPatientToast(`Merged ${mergedCount} duplicate contact${mergedCount > 1 ? 's' : ''}.`);
    } catch (err: any) {
      setMergeError(err?.response?.data?.error || err?.message || 'Failed to merge contacts');
    } finally {
      setMergeLoading(false);
    }
  };

  const selectedLabFax = unfiledLabFaxes.find((lab) => lab.id === selectedLabFaxId) || null;
  const labAttachment =
    selectedLabFax?.spruce_message_id && selectedLabFax?.spruce_attachment_id
      ? {
          id: selectedLabFax.spruce_attachment_id,
          filename: selectedLabFax.filename || 'Lab fax',
          contentType: selectedLabFax.mimetype || 'application/octet-stream',
          size: selectedLabFax.file_size,
          source: 'spruce',
        }
      : null;
  const labConversationId = getSpruceConversationId(null);
  const labMessage = selectedLabFax?.spruce_message_id
    ? { spruceMessageId: selectedLabFax.spruce_message_id, spruceConversationId: labConversationId }
    : null;
  const labTotalPages = labFaxDetail?.page_count || selectedLabFax?.page_count || null;

  return (
    <>
      <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: { minHeight: isMobile ? '100dvh' : '80vh' }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Linked patients: avatar + name route to the full profile
                (Details / Documents / Billing / History) with Back to task. */}
            <Tooltip title={chartKey ? 'Open patient profile' : ''} disableHoverListener={!chartKey}>
              <Avatar
                onClick={chartKey ? handleOpenProfile : undefined}
                sx={{
                  width: 48,
                  height: 48,
                  bgcolor: 'primary.main',
                  cursor: chartKey ? 'pointer' : 'default',
                  '&:hover': chartKey ? { boxShadow: 3 } : undefined,
                }}
              >
                {displayTask.patientName.charAt(0)}
              </Avatar>
            </Tooltip>
            <Box sx={{ minWidth: 0 }}>
              {/* Where the task is, beside the name, in words and in a colour
                  that means it; urgency only when it is urgent; the quiet
                  facts on one line beneath. Three grey chips of equal weight
                  — one of them the raw enum "inprogress" — said the least on
                  the line everyone reads first. Words in taskHeaderFacts.ts. */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography
                  variant="h6"
                  onClick={chartKey ? handleOpenProfile : undefined}
                  sx={{
                    fontWeight: 600,
                    lineHeight: 1.25,
                    cursor: chartKey ? 'pointer' : 'default',
                    '&:hover': chartKey ? { color: 'primary.main', textDecoration: 'underline' } : undefined,
                  }}
                >
                  {displayTask.patientName}
                </Typography>
                <Chip
                  label={statusChip(task.status).label}
                  color={statusChip(task.status).color}
                  size="small"
                  variant={statusChip(task.status).color === 'default' ? 'outlined' : 'filled'}
                  sx={{ fontWeight: 600 }}
                />
                {priorityChip(task.priority) && (
                  <Chip
                    label={priorityChip(task.priority)!.label}
                    color={priorityChip(task.priority)!.color}
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                )}
                {(() => {
                  const chip = turnChip((displayTask as any)?.turn ?? task.turn);
                  return chip ? (
                    <Chip
                      label={chip.label}
                      color={chip.tone === 'office' ? 'warning' : 'default'}
                      variant="outlined"
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  ) : null;
                })()}
              </Box>
              {headerSubtitle({ category: task.category, modality: task.modality, priority: task.priority }) && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                  {headerSubtitle({ category: task.category, modality: task.modality, priority: task.priority })}
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Grid container spacing={3}>
          {/* Left Column - Messages & Response */}
          <Grid size={{ xs: 12, md: 7 }}>
            {/* AI Analysis */}
            <Paper sx={{ p: 2, mb: 2, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AIIcon color="primary" />
                  <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600 }}>
                    AI Analysis
                  </Typography>
                </Box>
                <IconButton size="small" onClick={() => setIsAiCollapsed((prev) => !prev)}>
                  {isAiCollapsed ? <ExpandIcon fontSize="small" /> : <CollapseIcon fontSize="small" />}
                </IconButton>
              </Box>
              
              <Collapse in={!isAiCollapsed} timeout={200}>
                <Box>
                  {/* Urgency Score Bar */}
                  {task.aiAnalysis?.urgency !== undefined && (
                    <Box sx={{ mb: 1.5, mt: 0.5 }}>
                      <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>
                        Urgency Score:
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
                        <Box 
                          sx={{ 
                            flex: 1, 
                            height: 8, 
                            bgcolor: '#e5e7eb', 
                            borderRadius: 4,
                            overflow: 'hidden',
                            position: 'relative'
                          }}
                        >
                          <Box 
                            sx={{ 
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              height: '100%',
                              width: `${Math.min(100, Math.max(0, task.aiAnalysis.urgency))}%`,
                              bgcolor: task.aiAnalysis.urgency >= 70 ? '#dc2626' : 
                                       task.aiAnalysis.urgency >= 40 ? '#f59e0b' : '#10b981',
                              borderRadius: 4,
                              transition: 'width 0.3s ease'
                            }}
                          />
                        </Box>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            fontWeight: 600,
                            color: task.aiAnalysis.urgency >= 70 ? '#dc2626' : 
                                   task.aiAnalysis.urgency >= 40 ? '#f59e0b' : '#10b981',
                            minWidth: '45px'
                          }}
                        >
                          {task.aiAnalysis.urgency}/100
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {task.aiAnalysis?.keyPoints && task.aiAnalysis.keyPoints.length > 0 && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>
                        Key Points:
                      </Typography>
                      <Box sx={{ mt: 0.5, pl: 1 }}>
                        {task.aiAnalysis.keyPoints.map((point: string, idx: number) => (
                          <Typography key={idx} variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                            • {point}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  )}
                  {task.aiAnalysis?.suggestedResponse && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>
                        Suggested Response:
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                        {task.aiAnalysis.suggestedResponse}
                      </Typography>
                    </Box>
                  )}
                  {task.aiAnalysis?.recommendedAction && (
                    <Box>
                      <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>
                        Recommended Action:
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {task.aiAnalysis.recommendedAction}
                      </Typography>
                    </Box>
                  )}
                  {!task.aiAnalysis?.keyPoints?.length && !task.aiAnalysis?.suggestedResponse && (
                    <Typography variant="body2" color="text.secondary">
                      {task.aiSummary || 'AI analysis in progress...'}
                    </Typography>
                  )}
                </Box>
              </Collapse>
              {isAiCollapsed && (
                <Typography variant="caption" color="text.secondary">
                  Expand to view AI analysis.
                </Typography>
              )}
            </Paper>

            {/* Original Message */}
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Original Message
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
              >
                {displayTask.messageText}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Received: {formatDateTime(displayTask.createdAt, dateTimeFormat)}
              </Typography>
            </Paper>

            {/* Conversation History */}
            <Paper
              ref={conversationScrollRef}
              sx={{
                p: 2,
                mb: 2,
                maxHeight: 300,
                overflowY: 'auto',
                overflowX: 'hidden',
                // Messages contain pasted links. A URL has no break points, so
                // without this the bubble grows past the modal and the whole
                // conversation scrolls sideways on a phone.
                '& *': { overflowWrap: 'anywhere', wordBreak: 'break-word' },
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Conversation History
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {/* Set by the poll, cleared the moment they touch the thread.
                      A marker, not a nag. */}
                  {liveChange && (
                    <Chip
                      label={liveChange}
                      size="small"
                      color="success"
                      onClick={() => setLiveChange('')}
                      sx={{ fontWeight: 600 }}
                    />
                  )}
                  {displayTask.messageCount && displayTask.messageCount > 1 && (
                    <Chip
                      // "18 messages" plus the Spruce control pushed the heading
                      // onto two lines at 375px. The board cards already say
                      // "msgs" on their own, so the short form is the app's own
                      // word rather than a new one invented for the squeeze.
                      label={
                        <>
                          {displayTask.messageCount}
                          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                            {' messages'}
                          </Box>
                          <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                            {' msgs'}
                          </Box>
                        </>
                      }
                      size="small"
                      color="info"
                      variant="outlined"
                    />
                  )}
                  {/* The escape hatch, moved here from Quick Actions. Almost
                      everything a patient sends previews inline; when something
                      does not, this is the thread you were looking at when you
                      noticed. Kept at the Spruce app root rather than a guessed
                      conversation deep-link, so it never 404s — worth revisiting
                      if Spruce's URL scheme is ever confirmed. */}
                  <ActionHint title="Opens Spruce in a new tab, for anything in this thread that does not show here — an attachment that will not preview, for instance. It opens the Spruce inbox, not this conversation, so you will need to find the thread there.">
                    <Button
                      variant="text"
                      size="small"
                      color="inherit"
                      startIcon={<OpenInNewIcon fontSize="small" />}
                      onClick={() => window.open('https://app.sprucehealth.com', '_blank', 'noopener,noreferrer')}
                      sx={{
                        color: 'text.secondary',
                        // Collapses to the icon alone on a phone, so it needs a
                        // deliberate touch target — at its natural size it came
                        // out 26px square, which is a miss waiting to happen.
                        minWidth: { xs: 36, sm: 0 },
                        minHeight: { xs: 36, sm: 0 },
                        px: { xs: 0.5, sm: 1 },
                        '& .MuiButton-startIcon': { mr: { xs: 0, sm: 0.5 }, ml: 0 },
                      }}
                      aria-label="Open in Spruce"
                    >
                      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                        Open in Spruce
                      </Box>
                    </Button>
                  </ActionHint>
                </Box>
              </Box>
              <List dense>
                {(() => {
                  // Combine conversation messages and internal notes for unified history
                  const allItems: any[] = [];
                  
                  // Add conversation messages
                  if (displayTask.conversationMessages && displayTask.conversationMessages.length > 0) {
                    displayTask.conversationMessages.forEach((msg: any) => {
                      const isSpruceInternalNote = msg?.isInternalNote ||
                        msg?.messageType === 'internal_note' ||
                        msg?.source === 'spruce_internal' ||
                        msg?.direction === 'none' ||
                        ((msg?.direction === 'inbound' || msg?.direction === 'none') && msg?.senderName === 'Staff');
                      allItems.push({
                        ...msg,
                        type: isSpruceInternalNote ? 'internal_note' : 'message',
                        source: msg?.source || (isSpruceInternalNote ? 'spruce_internal' : msg?.source),
                        timestamp: new Date(msg.createdAt).getTime(),
                      });
                    });
                  }
                  
                  // Add internal notes
                  if (displayTask.internalNotes && displayTask.internalNotes.length > 0) {
                    displayTask.internalNotes.forEach((note: any) => {
                      allItems.push({
                        id: note.id,
                        content: note.text,
                        createdAt: note.createdAt,
                        senderName: note.author || 'Staff',
                        source: note.source || 'internal',
                        type: 'internal_note',
                        timestamp: new Date(note.createdAt).getTime(),
                      });
                    });
                  }
                  
                  // Sort by timestamp (oldest first; newest at bottom, Spruce-style)
                  allItems.sort((a, b) => a.timestamp - b.timestamp);
                  
                  // If no items, show original message
                  if (allItems.length === 0) {
                    return (
                      <ListItem sx={{ bgcolor: 'grey.100', borderRadius: 1, mb: 1 }}>
                        <ListItemText
                          primary={displayTask.messageText}
                          secondary={`${displayTask.patientName} • ${formatDateTime(displayTask.createdAt, dateTimeFormat)}`}
                        />
                      </ListItem>
                    );
                  }
                  
                  return allItems.map((item: any, idx: number) => {
                    // Internal note styling
                    if (item.type === 'internal_note') {
                      const badgeColor = item.source === 'doctor' ? '#9c27b0' : 
                                         item.source === 'spruce_internal' ? '#1976d2' : '#6b7280';
                      const badgeText = item.source === 'doctor' ? 'Doctor' :
                                        item.source === 'spruce_internal' ? 'Spruce' : 'Staff';
                      return (
                        <ListItem 
                          key={item.id || idx} 
                          sx={{ 
                            bgcolor: '#faf5ff',
                            borderRadius: 1, 
                            mb: 1,
                            borderLeft: '3px solid #9c27b0',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'stretch',
                            position: 'relative',
                          }}
                        >
                          <Box sx={{
                            position: 'absolute',
                            top: 6,
                            left: 8,
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            bgcolor: '#e53935',
                            boxShadow: '0 0 0 2px #fff',
                          }} />
                          <Box sx={{ 
                            position: 'absolute', 
                            top: 4, 
                            right: 8, 
                            bgcolor: badgeColor,
                            color: 'white',
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                            fontSize: '0.65rem',
                            fontWeight: 600,
                          }}>
                            📝 {badgeText}
                          </Box>
                          <ListItemText
                            primary={item.content}
                            secondary={`${item.senderName} • ${formatDateTime(item.createdAt, dateTimeFormat)}`}
                            sx={{ pr: 8 }}
                          />
                        </ListItem>
                      );
                    }
                    
                    // Regular message styling
                    return (
                      <ListItem 
                        key={item.id || idx} 
                        sx={{ 
                          bgcolor: item.direction === 'outbound' ? 'success.50' : 'grey.100', 
                          borderRadius: 1, 
                          mb: 1,
                          borderLeft: item.direction === 'outbound' ? '3px solid #10b981' : '3px solid #3b82f6',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'stretch'
                        }}
                      >
                        <ListItemText
                          primary={item.content}
                          secondary={`${item.senderName || displayTask.patientName} • ${formatDateTime(item.createdAt, dateTimeFormat)}`}
                        />
                        {item.attachments && item.attachments.length > 0 && (
                          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {item.attachments.map((attachment: MessageAttachment, attachmentIndex: number) => (
                              <Box
                                key={`${attachment.id || attachment.filename || attachmentIndex}`}
                                sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
                              >
                                <AttachIcon fontSize="small" />
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {attachment.filename || 'Attachment'}
                                </Typography>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => handlePreviewAttachment(attachment, item)}
                                >
                                  View
                                </Button>
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => handleDownloadAttachment(attachment, item)}
                                >
                                  Download
                                </Button>
                                {attachment.transcription && (
                                  <Typography variant="caption" color="text.secondary">
                                    Transcription: {attachment.transcription}
                                  </Typography>
                                )}
                              </Box>
                            ))}
                          </Box>
                        )}
                      </ListItem>
                    );
                  });
                })()}
              </List>
            </Paper>

            {/* Response Section — the one box. The scheduling panel on the right
                writes into it and the single Send below carries the offered
                times with the text. */}
            <Paper sx={{ p: 2, mb: 2 }} ref={replyBoxRef}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Send Response
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {schedulingStatus === 'pending_approval' && (
                    <ActionHint title="Removes the AI-written draft from the box and marks it declined, so this task stops waiting for approval. Nothing is sent.">
                      <Button size="small" variant="text" color="inherit" onClick={handleDiscardAiDraft} disabled={aiSchedulingLoading} sx={{ textTransform: 'none' }}>
                        Discard AI draft
                      </Button>
                    </ActionHint>
                  )}
                  <Button
                    startIcon={<AIIcon />}
                    variant="outlined"
                    size="small"
                    onClick={handleUseAISuggestion}
                  >
                    Use AI Suggestion
                  </Button>
                </Box>
              </Box>
              <TextField
                fullWidth
                multiline
                rows={4}
                placeholder="Type your response..."
                value={response}
                onChange={(e) => setResponse(e.target.value)}
                sx={{ mb: 2 }}
              />
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileSelected}
                style={{ display: 'none' }}
              />
              {pendingAttachments.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}>
                    Attached files (will be included on send):
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {pendingAttachments.map((attachment, idx) => (
                      <Box key={`${attachment.downloadToken || attachment.filename || idx}`} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AttachIcon fontSize="small" />
                        <Typography variant="body2">
                          {attachment.filename || 'Attachment'}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
              {sendError && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  {sendError}
                </Alert>
              )}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                {/* approve-draft cannot carry a file, so while the AI is waiting
                    on a confirmation there is nothing an attachment could ride on. */}
                {!isConfirmationDraft && (
                  <Button
                    variant="outlined"
                    startIcon={<AttachIcon />}
                    onClick={handleAttachClick}
                    disabled={isUploadingAttachment || isSending}
                  >
                    {isUploadingAttachment ? 'Uploading...' : 'Attach'}
                  </Button>
                )}
                <ActionHint title={sendResponseHint({ hasSlots: !!plannedSend.slots?.length })}>
                  <Button
                    variant="contained"
                    startIcon={isSending ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                    onClick={handleSendResponse}
                    disabled={isSending || !response.trim()}
                  >
                    {isSending ? 'Sending…' : 'Send Response'}
                  </Button>
                </ActionHint>
              </Box>
            </Paper>

            {/* What the task is, and its notes — under the reply box.
                History, so it is not undone by accident: In Progress /
                Complete once sat at the bottom of the right column, under a
                scheduling panel that grows (on a phone, three screens down);
                then first in the right column; then, at the team's request,
                here under the reply box — where they were below the fold on
                a laptop. They are pinned to the foot of the dialog now (after
                DialogContent), which is below the communication window and
                on screen at every size.
                Seven full-width buttons once stacked here. Measured before
                cutting: across 200 tasks, none had ever been assigned and
                none had a follow-up date — so the two state buttons stay and
                the rest are one menu away. Category is a compact select: a
                field set once and then read does not need a card of its own. */}
            <Paper sx={{ p: 2, mb: { xs: 2, md: 0 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Typography variant="caption" color="text.secondary" sx={{ flex: { xs: '0 0 auto' } }}>Category</Typography>
                <FormControl size="small" sx={{ minWidth: 150, flex: { xs: '1 1 160px', sm: '0 0 auto' } }}>
                  <Select
                    inputProps={{ 'aria-label': 'Category' }}
                      value={task.category || ''}
                    onChange={(e) => handleCategoryChange(e.target.value as TaskCategory)}
                  >
                    {(['clinical', 'admin', 'appointment', 'general'] as TaskCategory[]).map((cat) => (
                      <MenuItem key={cat} value={cat} sx={{ textTransform: 'capitalize' }}>
                        {cat.charAt(0).toUpperCase() + cat.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button size="small" onClick={() => setNotesOpen((v) => !v)} sx={{ textTransform: 'none', ml: 'auto' }}
                  aria-expanded={notesOpen || Boolean(internalNote.trim())}>
                  {notesOpen || internalNote.trim() ? 'Hide note' : 'Add a note…'}
                  {displayTask.internalNotes && displayTask.internalNotes.length > 0 && !(notesOpen || internalNote.trim()) && (
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                      ({displayTask.internalNotes.length})
                    </Typography>
                  )}
                </Button>
              </Box>

              <Collapse in={notesOpen || Boolean(internalNote.trim())}>
              <Box sx={{ pt: 1.5 }}>
              <TextField
                fullWidth
                multiline
                rows={2}
                placeholder="Add an internal note... (Cmd/Ctrl+Enter to save)"
                value={internalNote}
                onChange={(e) => setInternalNote(e.target.value)}
                onKeyDown={(e) => {
                  if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && internalNote.trim()) {
                    e.preventDefault();
                    handleAddNote();
                  }
                }}
                size="small"
                sx={{ mb: 1 }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleAddNote}
                  disabled={!internalNote.trim() || isLoading}
                  startIcon={<SaveIcon fontSize="small" />}
                  sx={{ textTransform: 'none' }}
                >
                  Add Note
                </Button>
                {noteError && (
                  <Typography variant="caption" color="error">{noteError}</Typography>
                )}
              </Box>
              </Box>
              </Collapse>

              {/* Existing Notes */}
              {displayTask.internalNotes && displayTask.internalNotes.length > 0 && (
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {(displayTask.internalNotes as InternalNote[]).map((note, noteIdx) => {
                    const isEditing = editingNoteId === note.id;
                    const isBusy = noteBusyId === note.id;
                    const editCount = Array.isArray(note.edits) ? note.edits.length : 0;
                    const createdIso = note.createdAt || note.at || '';
                    const editedIso = note.lastEditedAt || '';
                    const hasId = !!note.id;
                    const isAuthor = currentUserId != null && note.authorId != null && note.authorId === currentUserId;
                    const isLegacy = note.authorId == null;
                    const canMutate = hasId && (isAdmin || isAuthor || isLegacy);
                    return (
                      <Paper
                        key={note.id || `legacy-${noteIdx}`}
                        variant="outlined"
                        sx={{
                          p: 1.25,
                          bgcolor: isEditing ? 'primary.50' : 'grey.50',
                          borderColor: isEditing ? 'primary.main' : 'divider',
                          position: 'relative',
                          '&:hover .note-actions': { opacity: 1 },
                          '& .note-actions': { opacity: { xs: 1, sm: isEditing ? 1 : 0 }, transition: 'opacity 120ms' },
                        }}
                      >
                        {isEditing ? (
                          <>
                            <TextField
                              value={noteDraft}
                              onChange={(e) => setNoteDraft(e.target.value)}
                              onKeyDown={(e) => {
                                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                                  e.preventDefault();
                                  handleSaveNoteEdit(note);
                                } else if (e.key === 'Escape') {
                                  e.preventDefault();
                                  handleCancelNoteEdit();
                                }
                              }}
                              fullWidth
                              multiline
                              minRows={2}
                              size="small"
                              autoFocus
                              sx={{ bgcolor: 'background.paper' }}
                            />
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                              <Button size="small" onClick={handleCancelNoteEdit} disabled={isBusy} sx={{ textTransform: 'none' }}>
                                Cancel
                              </Button>
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={isBusy ? <CircularProgress size={14} color="inherit" /> : <SaveIcon fontSize="small" />}
                                onClick={() => handleSaveNoteEdit(note)}
                                disabled={isBusy || !noteDraft.trim() || noteDraft.trim() === note.text}
                                sx={{ textTransform: 'none' }}
                              >
                                Save
                              </Button>
                            </Box>
                          </>
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {note.text}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5, flexWrap: 'wrap' }}>
                                <Typography variant="caption" color="text.secondary">
                                  {note.author || 'Unknown'} • {formatDateTime(createdIso, dateTimeFormat)}
                                </Typography>
                                {editCount > 0 && (
                                  <Tooltip title="View edit history">
                                    <Button
                                      size="small"
                                      onClick={(e) => openNoteHistory(e, note)}
                                      startIcon={<HistoryIcon sx={{ fontSize: 14 }} />}
                                      sx={{
                                        textTransform: 'none', minWidth: 0, py: 0, px: 0.5,
                                        fontSize: '0.7rem', fontStyle: 'italic',
                                        color: 'text.secondary',
                                      }}
                                    >
                                      edited{editedIso ? ` ${formatRelative(editedIso)}` : ''}
                                      {note.lastEditedBy ? ` by ${note.lastEditedBy}` : ''}
                                      {` · ${editCount} ${editCount === 1 ? 'revision' : 'revisions'}`}
                                    </Button>
                                  </Tooltip>
                                )}
                              </Box>
                            </Box>
                            {canMutate && (
                              <Box className="note-actions" sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                <Tooltip title="Edit note">
                                  <span>
                                    <IconButton size="small" onClick={() => handleStartNoteEdit(note)} disabled={isBusy}>
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title="Delete note">
                                  <span>
                                    <IconButton size="small" onClick={() => handleDeleteNote(note)} disabled={isBusy} color="error">
                                      {isBusy ? <CircularProgress size={14} /> : <DeleteIcon fontSize="small" />}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Box>
                            )}
                          </Box>
                        )}
                      </Paper>
                    );
                  })}
                </Box>
              )}

              <Popover
                open={Boolean(historyAnchor && historyNote)}
                anchorEl={historyAnchor}
                onClose={closeNoteHistory}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{ sx: { p: 1.5, maxWidth: 420, maxHeight: 360 } }}
              >
                {historyNote && (
                  <>
                    <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>
                      Edit history
                    </Typography>
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ p: 1, borderRadius: 1, bgcolor: 'primary.50', borderLeft: '3px solid', borderColor: 'primary.main' }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.dark' }}>
                          Current{historyNote.lastEditedBy ? ` · ${historyNote.lastEditedBy}` : ''}{historyNote.lastEditedAt ? ` · ${formatDateTime(historyNote.lastEditedAt, dateTimeFormat)}` : ''}
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', mt: 0.25 }}>
                          {historyNote.text}
                        </Typography>
                      </Box>
                      {[...(historyNote.edits || [])].reverse().map((ed, i) => (
                        <Box key={i} sx={{ p: 1, borderRadius: 1, bgcolor: 'grey.50', borderLeft: '3px solid', borderColor: 'divider' }}>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                            {ed.editor || 'Unknown'} · {formatDateTime(ed.at, dateTimeFormat)}
                          </Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', mt: 0.25 }}>
                            {ed.text}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </>
                )}
              </Popover>
              <Menu
                anchorEl={taskActionsAnchor}
                open={Boolean(taskActionsAnchor)}
                onClose={() => setTaskActionsAnchor(null)}
              >
                {/* The MenuItem must stay a direct child of the list. Wrapping
                    each one in a tooltip span made <ul> hold three <span>s and
                    no <li>, which is invalid and — the part that matters —
                    stopped arrow keys moving through the menu at all. The
                    tooltip goes on the item itself, and a disabled item skips it
                    rather than reintroducing a wrapper for the one case. */}
                {overflowActions({ isRecallTask, deleting: deletingTask }).map((action) => {
                  const item = (
                    <MenuItem
                      key={action.key}
                      disabled={action.disabled}
                      onClick={() => {
                        setTaskActionsAnchor(null);
                        if (action.key === 'assign') handleOpenAssignDialog();
                        if (action.key === 'snooze') setFollowUpDialogOpen(true);
                        if (action.key === 'profile') handleOpenProfile();
                        if (action.key === 'delete') setConfirmDeleteTask(true);
                      }}
                      sx={action.destructive ? { color: 'error.main' } : undefined}
                    >
                      {action.label}
                    </MenuItem>
                  );
                  if (action.disabled) return item;
                  return (
                    <Tooltip key={action.key} title={action.hint} placement="left" arrow enterDelay={400}>
                      {item}
                    </Tooltip>
                  );
                })}
              </Menu>
            </Paper>
          </Grid>

          {/* Right Column - Patient Info & Actions */}
          <Grid size={{ xs: 12, md: 5 }}>

            {/* A patient saying they are not coming. Shown above the
                scheduling panel because releasing the slot comes before
                offering new times. */}
            {/* Once the booking has actually been cancelled the banner's job
                is done; the Cancelled state below says what comes next. Two
                panels saying "nothing booked" was one too many. */}
            {cancellation?.looksLikeCancellation && !cancellationDismissed
              && !(aiScheduling?.status === 'appointment_cancelled' && cancellation.upcomingAppointments.length === 0) && (() => {
              // Named by kind: a reschedule is a cancellation plus a rebooking,
              // and heading it "cancellation" sent the office to the wrong
              // next step. The words live in cancellationBanner.ts, tested.
              const copy = cancellationBannerCopy(cancellation.kind, cancellation.upcomingAppointments.length > 0);
              return (
              <Paper sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'warning.light' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {copy.heading}
                  </Typography>
                  <Chip size="small" color="warning" variant="outlined" label="check" />
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                  {copy.caption}
                </Typography>

                {copy.nothingBooked ? (
                  <Typography variant="body2" color="text.secondary">
                    {copy.nothingBooked}
                  </Typography>
                ) : (
                  <>
                    <RadioGroup
                      value={chosenAppointment}
                      onChange={(e) => setChosenAppointment(e.target.value)}
                    >
                      {cancellation.upcomingAppointments.map((appointment) => (
                        <FormControlLabel
                          key={appointment.id}
                          value={appointment.id}
                          control={<Radio size="small" />}
                          label={
                            <Typography variant="body2">
                              {formatDateOnly(new Date(appointment.startTime), dateTimeFormat)}
                              {' at '}
                              {formatTimeOnly(new Date(appointment.startTime), dateTimeFormat, false)}
                              {appointment.provider ? ` — ${appointment.provider}` : ''}
                              {appointment.appointmentType ? ` (${appointment.appointmentType})` : ''}
                            </Typography>
                          }
                        />
                      ))}
                    </RadioGroup>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                      <Button
                        size="small"
                        variant="contained"
                        color="warning"
                        disabled={!chosenAppointment || cancelBusy}
                        onClick={handleCancelAppointment}
                        sx={{ textTransform: 'none' }}
                      >
                        {copy.action}
                      </Button>
                      <Button
                        size="small"
                        sx={{ textTransform: 'none' }}
                        onClick={() => setCancellationDismissed(true)}
                      >
                        {copy.dismiss}
                      </Button>
                    </Box>
                  </>
                )}
              </Paper>
              );
            })()}

            {/* Patient Information */}
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                Patient Information
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {/* Name + PTID on same line */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon fontSize="small" color="action" />
                  {isEditingPatient ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                          size="small"
                          label="First name"
                          value={patientFirstName}
                          onChange={(e) => setPatientFirstName(e.target.value)}
                          fullWidth
                        />
                        <TextField
                          size="small"
                          label="Last name"
                          value={patientLastName}
                          onChange={(e) => setPatientLastName(e.target.value)}
                          fullWidth
                        />
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={handleSavePatientName}
                          disabled={patientSaveLoading}
                        >
                          {patientSaveLoading ? 'Saving...' : 'Save'}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            const { firstName, lastName } = splitPatientName(displayTask.patientName);
                            setPatientFirstName(firstName);
                            setPatientLastName(lastName);
                            setPatientSaveError(null);
                            setIsEditingPatient(false);
                          }}
                        >
                          Cancel
                        </Button>
                      </Box>
                      {patientSaveError && (
                        <Typography variant="caption" color="error">
                          {patientSaveError}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      {chartKey ? (
                        <>
                          {/* Linked: the chip routes to the full patient profile
                              (PT Details keeps the quick chart drawer). */}
                          <Tooltip title="Open patient profile">
                            <Chip
                              icon={<PersonIcon />}
                              size="small"
                              color="primary"
                              variant="outlined"
                              onClick={handleOpenProfile}
                              label={`${displayTask.patientName}${task.internalPatientId ? ` · ${task.internalPatientId}` : ''}`}
                              sx={{ fontWeight: 600, maxWidth: '100%' }}
                            />
                          </Tooltip>
                          <Tooltip title="Link a different patient">
                            <IconButton size="small" onClick={() => { setLinkError(null); setLinkPickerOpen(true); }}>
                              <SwapIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : (
                        <>
                          <Typography variant="body2">{displayTask.patientName}</Typography>
                          <Chip
                            icon={<PersonIcon />}
                            size="small"
                            variant="outlined"
                            onClick={() => { setLinkError(null); setLinkPickerOpen(true); }}
                            label="Link patient"
                            sx={{ borderStyle: 'dashed' }}
                          />
                        </>
                      )}
                      {isUnknownName(displayTask.patientName) && (
                        <Tooltip title="Edit patient name">
                          <IconButton size="small" onClick={() => setIsEditingPatient(true)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  )}
                  {task.proxyIntake?.active && (
                    <Box sx={{ mt: 1, p: 1, borderRadius: 1, bgcolor: task.proxyIntake?.unresolved ? 'warning.light' : 'action.hover' }}>
                      <Typography variant="caption" display="block" fontWeight={600}>
                        {task.proxyIntake?.unresolved
                          ? 'Proxy intake — confirm who this patient is (Link patient)'
                          : 'Proxy intake — linked from staff SMS'}
                      </Typography>
                      {task.proxyIntake?.parsedPatientName && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Parsed name: {task.proxyIntake.parsedPatientName}
                          {task.proxyIntake.matchStatus ? ` · ${task.proxyIntake.matchStatus}` : ''}
                        </Typography>
                      )}
                      {task.proxyIntake?.proxySenderPhone && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          From proxy phone: {task.proxyIntake.proxySenderPhone}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
                {/* Phone */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PhoneIcon fontSize="small" color="action" />
                  <Typography variant="body2">
                    {(() => {
                      const hasPhone = task.patientPhone && task.patientPhone !== 'Unknown';
                      const num = hasPhone ? task.patientPhone : resolvedPhone?.phone || '';
                      if (!num) return 'Unknown';
                      return (
                        <>
                          {num}
                          {!hasPhone && resolvedPhone?.label && (
                            <Typography
                              component="span"
                              variant="caption"
                              color="text.secondary"
                              sx={{ ml: 0.75 }}
                            >
                              ({resolvedPhone.label})
                            </Typography>
                          )}
                        </>
                      );
                    })()}
                  </Typography>
                </Box>
                {((displayTask as Task)?.assignedToUser?.name || (displayTask as Task)?.assignedToUser?.email || (displayTask as Task)?.assignedTo) && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AssignIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      Assigned to:{' '}
                      {(displayTask as Task)?.assignedToUser?.name ||
                        (displayTask as Task)?.assignedToUser?.email ||
                        (displayTask as Task)?.assignedTo}
                    </Typography>
                  </Box>
                )}
                {/* Received date */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    Received: {formatDateTime(displayTask.createdAt, dateTimeFormat)}
                  </Typography>
                </Box>
                {(displayTask as Task)?.followUpDate && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FollowUpIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      Follow-up:{' '}
                      {new Date((displayTask as Task).followUpDate || '').toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>

            {(labMatch || unfiledLabFaxes.length > 0) && (
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                  Lab Fax Assignment
                </Typography>
                {labMatch && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                    <Chip
                      label={`Lab Sender Detected: ${labMatch.match?.name || 'Unknown'}${labMatch.matchType ? ` (${labMatch.matchType})` : ''}`}
                      color="warning"
                      variant="outlined"
                      size="small"
                    />
                    {labMatch.match?.category && (
                      <Typography variant="caption" color="text.secondary">
                        Category: {labMatch.match.category}
                      </Typography>
                    )}
                  </Box>
                )}

                {unfiledLabFaxes.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No pending lab faxes to assign.
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        Pending Faxes
                      </Typography>
                      <List dense sx={{ maxHeight: 160, overflow: 'auto', mt: 1 }}>
                        {unfiledLabFaxes.map((fax) => (
                          <ListItemButton
                            key={fax.id}
                            selected={fax.id === selectedLabFaxId}
                            onClick={() => setSelectedLabFaxId(fax.id)}
                          >
                            <ListItemText
                              primary={fax.filename || 'Lab fax'}
                              secondary={`${formatFileSize(fax.file_size)} • ${fax.created_at ? formatDateTime(fax.created_at, dateTimeFormat) : 'Unknown date'}`}
                            />
                          </ListItemButton>
                        ))}
                      </List>
                    </Box>

                    {labFaxLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={20} />
                      </Box>
                    ) : selectedLabFax ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={!labAttachment || !labMessage}
                            onClick={() => labAttachment && labMessage && handlePreviewAttachment(labAttachment, labMessage)}
                          >
                            View
                          </Button>
                          <Button
                            size="small"
                            variant="text"
                            disabled={!labAttachment || !labMessage}
                            onClick={() => labAttachment && labMessage && handleDownloadAttachment(labAttachment, labMessage)}
                          >
                            Download
                          </Button>
                        </Box>

                        {labError && (
                          <Typography variant="caption" color="error">
                            {labError}
                          </Typography>
                        )}

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setShowSplitPanel((prev) => !prev)}
                            disabled={!selectedLabFaxId || labSplitLoading}
                          >
                            {showSplitPanel ? 'Hide Split' : 'Split PDF'}
                          </Button>
                          {labTotalPages ? (
                            <Typography variant="caption" color="text.secondary">
                              Total pages: {labTotalPages}
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              Page count unavailable
                            </Typography>
                          )}
                        </Box>

                        {showSplitPanel && (
                          <Box
                            sx={{
                              mt: 1,
                              p: 1,
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 1,
                            }}
                          >
                            {labSplitRows.map((row) => (
                              <Box key={row.id} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <TextField
                                  size="small"
                                  label="Start"
                                  value={row.startPage}
                                  onChange={(event) => updateSplitRow(row.id, 'startPage', event.target.value)}
                                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', min: 1 }}
                                />
                                <TextField
                                  size="small"
                                  label="End"
                                  value={row.endPage}
                                  onChange={(event) => updateSplitRow(row.id, 'endPage', event.target.value)}
                                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', min: 1 }}
                                />
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => removeSplitRow(row.id)}
                                  disabled={labSplitRows.length === 1 || labSplitLoading}
                                >
                                  Remove
                                </Button>
                              </Box>
                            ))}
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button size="small" variant="outlined" onClick={addSplitRow} disabled={labSplitLoading}>
                                Add Split
                              </Button>
                              <Button
                                size="small"
                                variant="contained"
                                onClick={handleCreateSplits}
                                disabled={labSplitLoading || !selectedLabFaxId}
                              >
                                {labSplitLoading ? 'Splitting...' : 'Create Splits'}
                              </Button>
                            </Box>
                            {labSplitError && (
                              <Typography variant="caption" color="error">
                                {labSplitError}
                              </Typography>
                            )}
                          </Box>
                        )}

                        <Typography variant="caption" sx={{ fontWeight: 600, mt: 1 }}>
                          OCR Extract
                        </Typography>
                        <Box sx={{ bgcolor: 'grey.50', borderRadius: 1, p: 1, maxHeight: 120, overflow: 'auto' }}>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {labFaxDetail?.ocr_text || 'No OCR text available yet.'}
                          </Typography>
                        </Box>

                        <Divider sx={{ my: 1 }} />

                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          Assign to Patient
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <TextField
                            size="small"
                            placeholder="Search patient name, phone, or ID"
                            value={labSearchQuery}
                            onChange={(e) => setLabSearchQuery(e.target.value)}
                            fullWidth
                          />
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={handleLabSearch}
                              disabled={labSearchLoading || !labSearchQuery.trim()}
                            >
                              {labSearchLoading ? '...' : 'Search'}
                            </Button>
                            <InfoTooltipIcon title="Searches for matching patients and shows the results list." />
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Button
                              size="small"
                              variant="contained"
                              disabled={labSearchLoading || labAttachLoading || !labSearchQuery.trim() || !selectedLabFaxId}
                              onClick={() => handleSearchAndAttachLabFax(true)}
                            >
                              {labSearchLoading ? 'Searching...' : labAttachLoading ? 'Attaching...' : 'Search & Summarize'}
                            </Button>
                            <InfoTooltipIcon title="Runs the search. If exactly one match is found, it attaches this fax and generates an AI summary. If multiple matches, it shows the list." />
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={labSearchLoading || labAttachLoading || !labSearchQuery.trim() || !selectedLabFaxId}
                              onClick={() => handleSearchAndAttachLabFax(false)}
                            >
                              {labSearchLoading ? 'Searching...' : labAttachLoading ? 'Attaching...' : 'Search & Attach'}
                            </Button>
                            <InfoTooltipIcon title="Runs the search. If exactly one match is found, it attaches this fax without generating a summary. If multiple matches, it shows the list." />
                          </Box>
                        </Box>

                        {labSuggestedContacts.length > 0 && (
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Suggested Matches
                            </Typography>
                            {labSuggestedContacts.map((contact) => (
                              <Box key={contact.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                                <Typography variant="body2">
                                  {contact.fullName}
                                  {contact.phone ? ` • ${contact.phone}` : ''}
                                  {formatDob(contact.dateOfBirth) ? ` • DOB: ${formatDob(contact.dateOfBirth)}` : ''}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    disabled={labAttachLoading}
                                    onClick={() => handleAttachLabFax(contact.id, true)}
                                  >
                                    Attach & Summarize
                                  </Button>
                                  <InfoTooltipIcon title="Attaches this fax to the selected patient and generates an AI summary." />
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={labAttachLoading}
                                    onClick={() => handleAttachLabFax(contact.id, false)}
                                  >
                                    Attach Only
                                  </Button>
                                  <InfoTooltipIcon title="Attaches this fax to the selected patient without generating a summary." />
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        )}

                        {labSearchResults.length > 0 && (
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Search Results
                            </Typography>
                            {labSearchResults.map((contact) => (
                              <Box key={contact.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                                <Typography variant="body2">
                                  {contact.fullName}
                                  {contact.phone ? ` • ${contact.phone}` : ''}
                                  {formatDob(contact.dateOfBirth) ? ` • DOB: ${formatDob(contact.dateOfBirth)}` : ''}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    disabled={labAttachLoading}
                                    onClick={() => handleAttachLabFax(contact.id, true)}
                                  >
                                    Attach & Summarize
                                  </Button>
                                  <InfoTooltipIcon title="Attaches this fax to the selected patient and generates an AI summary." />
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={labAttachLoading}
                                    onClick={() => handleAttachLabFax(contact.id, false)}
                                  >
                                    Attach Only
                                  </Button>
                                  <InfoTooltipIcon title="Attaches this fax to the selected patient without generating a summary." />
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Select a fax to view details.
                      </Typography>
                    )}
                  </Box>
                )}
              </Paper>
            )}

            {showSchedulingPanel && (
              <Paper sx={{ p: 2, mb: 2, border: '1px solid #e0e7ff' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    AI Scheduling
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {/* The only thing that used to open the picker on a booked
                        task was the first press of the panel's Send, which is
                        gone. This is that press, named for what it does. */}
                    {schedulingStatus === 'appointment_confirmed' && !isRescheduleMode && (
                      <ActionHint title="Opens the time picker so you can offer this patient a different appointment. Nothing is sent by this press.">
                        <Button size="small" variant="outlined" onClick={() => setIsRescheduleMode(true)} sx={{ textTransform: 'none' }}>
                          Offer different times
                        </Button>
                      </ActionHint>
                    )}
                    <Chip label={schedulingStatusLabel} color={schedulingStatusColor as any} size="small" />
                  </Box>
                </Box>
                {/* The next step, not the state: the chip already says the
                    state. Body weight, not a caption — this is the sentence
                    to read before doing anything else in this panel. */}
                <Typography variant="body2" role="status" sx={{ display: 'block', mb: 1, fontWeight: 500 }}>
                  {nextStep}
                </Typography>
                {isConfirmationDraft && aiScheduling?.pendingSlot && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Selected slot
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <Chip size="small" label={formatSlotLabel(aiScheduling.pendingSlot, dateTimeFormat)} />
                    </Box>
                  </Box>
                )}

                {showTimePicker && (
                  <Box sx={{ mt: 2, p: 1.5, border: '1px dashed #dbeafe', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Find times
                      </Typography>
                      <ActionHint title="Opens the calendar on this date, filtered to this provider, so you can see the day around these times. A Back to task button brings you straight back here.">
                      <Button
                        size="small"
                        startIcon={<CalendarIcon fontSize="small" />}
                        sx={{ textTransform: 'none', flexShrink: 0 }}
                        onClick={() => {
                          const params = new URLSearchParams({ date: slotOverrideDate });
                          if (selectedMapping?.specialist?.id) {
                            params.set('provider', selectedMapping.specialist.id);
                          }
                          // Was window.open to a new tab, which left no way back
                          // to the task. Same in-app anchor the patient-chart and
                          // Contacts flows use: the dashboard reopens this task
                          // from ?taskId=.
                          navigate(`/calendar?${params.toString()}`, {
                            state: { returnTo: `/?taskId=${taskId}`, backLabel: 'Back to task' },
                          });
                        }}
                      >
                        Open in calendar
                      </Button>
                      </ActionHint>
                    </Box>
                    <Box
                      ref={pickerRef}
                      sx={{
                        display: 'flex',
                        gap: 1,
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        mb: 1,
                        // On a phone these controls each take the full width
                        // rather than squeezing side by side, where the
                        // provider name truncated mid-word.
                        '& > .MuiFormControl-root': { flex: { xs: '1 1 100%', sm: '0 1 auto' } },
                      }}
                    >
                      <ActionHint title="Which appointment type the times below are drawn from, and who with. Guessed from the patient's latest message — change it whenever the guess is wrong; it books nothing on its own.">
                      <TextField
                        select
                        size="small"
                        label="Service + Provider"
                        value={slotOverrideMappingId}
                        onChange={(e) => setSlotOverrideMappingId(e.target.value)}
                        sx={{ minWidth: 220 }}
                      >
                        {slotOverrideMappings.length === 0 && (
                          <MenuItem value="" disabled>
                            No providers configured
                          </MenuItem>
                        )}
                        {slotOverrideMappings.map((mapping) => (
                          <MenuItem key={mapping.id} value={mapping.id}>
                            {mapping.service.name} • {mapping.specialist.name}
                          </MenuItem>
                        ))}
                      </TextField>
                      </ActionHint>
                      <ActionHint title="The day the times come from. It opens on the next day this provider works — this practice does not take same-day bookings — and stays where you put it after that.">
                      <TextField
                        size="small"
                        type="date"
                        label="Date"
                        value={slotOverrideDate}
                        onChange={(e) => setSlotOverrideDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                      </ActionHint>
                      <ActionHint title="Show times for that one day, or for the whole week from it — useful when the patient has said any day suits.">
                      <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={slotOverrideRange}
                        onChange={(_, value) => value && setSlotOverrideRange(value)}
                      >
                        <ToggleButton value="day" sx={{ textTransform: 'none', px: 1.5 }}>One day</ToggleButton>
                        <ToggleButton value="week" sx={{ textTransform: 'none', px: 1.5 }}>Whole week</ToggleButton>
                      </ToggleButtonGroup>
                      </ActionHint>
                    </Box>
                    {pickingNote && (
                      <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'primary.dark', fontWeight: 600 }}>
                        {pickingNote}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        // Its own line on a phone, so the two buttons stay
                        // together instead of one being orphaned below.
                        sx={{ flex: { xs: '1 1 100%', sm: '0 0 auto' } }}
                      >
                        Or propose a full set:
                      </Typography>
                      <ActionHint title="Proposes the whole wellness visit at once: labs with the nurse, then the doctor at least three days later so the results are back. Both land in the draft list below — nothing is booked or sent.">
                      <Button size="small" variant="outlined" sx={{ textTransform: 'none' }}
                        disabled={proposingBundle !== null}
                        onClick={() => handleProposeBundle('wellness-exam')}>
                        {proposingBundle === 'wellness-exam' ? 'Proposing…' : 'Wellness exam'}
                      </Button>
                      </ActionHint>
                      <ActionHint title="Proposes the pre-op set in one morning: the labs with the EKG, then the visit straight after. If that morning is full it moves to the next one. Both land in the draft list below — nothing is booked or sent.">
                      <Button size="small" variant="outlined" sx={{ textTransform: 'none' }}
                        disabled={proposingBundle !== null}
                        onClick={() => handleProposeBundle('pre-op')}>
                        {proposingBundle === 'pre-op' ? 'Proposing…' : 'Pre-op'}
                      </Button>
                      </ActionHint>
                      {/* A second set, starting the day after the last one, so
                          the patient has a choice for each step. */}
                      {Object.entries(lastProposals).map(([key, steps]) => (
                        <ActionHint key={key} title="Proposes the next set after the one already listed, so the patient can choose between them. Nothing is booked or sent.">
                        <Button size="small" variant="text" sx={{ textTransform: 'none' }}
                          disabled={proposingBundle !== null}
                          onClick={() => handleProposeBundle(key, nextProposalFrom(steps))}>
                          {proposingBundle === key && lastProposals[key]
                            ? 'Proposing…'
                            : (key === 'pre-op' ? 'Another pre-op option' : 'Another wellness option')}
                        </Button>
                        </ActionHint>
                      ))}
                    </Box>
                    {slotOverrideError && (
                      <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
                        {slotOverrideError}
                      </Typography>
                    )}
                    {slotOverrideLoading ? (
                      <Typography variant="body2" color="text.secondary">
                        Finding times…
                      </Typography>
                    ) : manualSlotOptions.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No available slots for this date. Choose another date or provider.
                      </Typography>
                    ) : (
                      <>
                        <Box sx={{ bgcolor: 'grey.50', borderRadius: 1, p: 1, maxHeight: 260, overflowY: 'auto' }}>
                          {slotOptionsByDay.map(([day, options]) => (
                            <Box key={day} sx={{ mb: 1.25, '&:last-child': { mb: 0 } }}>
                              <Typography
                                variant="caption"
                                sx={{ display: 'block', fontWeight: 700, color: 'text.secondary', mb: 0.5 }}
                              >
                                {slotDayHeading(day, dateTimeFormat)}
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                {options.map((option) => {
                                  const picked = slotOverrideSelectedKeys.includes(option.key);
                                  return (
                                    <Chip
                                      key={option.key}
                                      size="small"
                                      icon={<TimeIcon />}
                                      // The formatted label first. This read
                                      // option.slot.time, which is the raw
                                      // 24-hour string from the API, so the chip
                                      // ignored the label built right beside it.
                                      label={formatClockString(option.slot.time, dateTimeFormat) || option.label}
                                      color={picked ? 'primary' : 'default'}
                                      variant={picked ? 'filled' : 'outlined'}
                                      onClick={() => handleToggleManualSlot(option.key)}
                                    />
                                  );
                                })}
                              </Box>
                            </Box>
                          ))}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                          <ActionHint title="Ticks every time shown above.">
                          <Button size="small" variant="text" onClick={handleSelectAllManualSlots}>
                            Select All
                          </Button>
                          </ActionHint>
                          <ActionHint title="Unticks the times above. Anything already in the draft list stays there.">
                          <Button size="small" variant="text" onClick={handleClearManualSlots}>
                            Clear
                          </Button>
                          </ActionHint>
                          <ActionHint title="Moves the ticked times into the list below and into your reply, which is what the patient would be offered.">
                          <Button size="small" variant="outlined" onClick={handleAddManualSlots}>
                            Add to the offer
                          </Button>
                          </ActionHint>
                        </Box>
                      </>
                    )}

                    {/* A time that is not in the list — 10:15, say. Three
                        lists and one button; the day is the Date field above. It is
                        checked against the calendar by the same rule the list
                        is built from, so it is held and booked like a listed
                        time, and refused (with the reason) if it clashes, is
                        outside the provider's hours or has passed. Shown even
                        when the list is empty: that is when it is needed. */}
                    {selectedMapping && (
                      <Box sx={{ mt: 0.75 }}>
                        {/* One quiet line until it is wanted. It stays open
                            while there is something typed or something to
                            read, so a refusal is never folded out of sight. */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: -0.75 }}>
                          <Button
                            size="small"
                            variant="text"
                            color="inherit"
                            onClick={() => {
                              const opening = !(typedTimeOpen || Boolean(typedTimeNote));
                              setTypedTimeOpen(opening);
                              if (!opening) setTypedTimeNote(null);
                            }}
                            aria-expanded={typedTimeOpen || Boolean(typedTimeNote)}
                            // The same words open or closed, so the lists
                            // beneath are never without their label; the
                            // arrow says which way it goes.
                            endIcon={
                              <ExpandMoreIcon
                                sx={{
                                  fontSize: '16px !important',
                                  transition: 'transform 150ms',
                                  transform: typedTimeOpen || typedTimeNote ? 'rotate(180deg)' : 'none',
                                }}
                              />
                            }
                            sx={{
                              textTransform: 'none', color: 'text.secondary', fontWeight: 400, fontSize: '0.75rem',
                              '& .MuiButton-endIcon': { ml: 0.25 },
                            }}
                          >
                            Need a time that is not listed?
                          </Button>
                          <Tooltip
                            title="Pick any time for the day chosen above. It is checked against the calendar like a listed time and held on a booking link the same way. A time that clashes, is outside the provider's hours or has passed is not added."
                            arrow
                            enterTouchDelay={0}
                            leaveTouchDelay={5000}
                          >
                            <InfoIcon sx={{ fontSize: 15, color: 'text.disabled', cursor: 'help' }} />
                          </Tooltip>
                        </Box>
                        <Collapse in={typedTimeOpen || Boolean(typedTimeNote)} unmountOnExit>
                          {/* Hour, minutes, AM or PM, then Add. Native lists:
                              a dropdown on a desktop, the phone's own wheel on
                              a phone. They share the row and shrink together,
                              so Add never drops to a line of its own. */}
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              mt: 0.5,
                              // Hour and minutes are two digits; AM/PM is two
                              // wide letters and needs more room, or a phone
                              // (which draws these at 16px) clips it to "AN".
                              // Small on purpose: this is a side road off
                              // the list above, not a second form. The lists
                              // are only as wide as what they hold.
                              '& .MuiTextField-root': { flex: '0 1 56px', minWidth: 50 },
                              '& .MuiTextField-root.ampm': { flex: '0 1 68px', minWidth: 66 },
                              '& select': {
                                py: '4px',
                                pl: 1,
                                pr: '20px !important',
                                fontSize: { sm: '0.8125rem' },
                                lineHeight: 1.5,
                              },
                              '& .MuiNativeSelect-icon': { right: 0, fontSize: '1.15rem' },
                            }}
                          >
                            <TextField
                              select
                              size="small"
                              SelectProps={{ native: true }}
                              value={pickedHour}
                              onChange={(e) => { setPickedHour(Number(e.target.value)); if (typedTimeNote) setTypedTimeNote(null); }}
                              inputProps={{ 'aria-label': 'Hour' }}
                            >
                              {HOUR_CHOICES.map((h) => <option key={h} value={h}>{h}</option>)}
                            </TextField>
                            <Typography variant="body2" color="text.secondary" sx={{ mx: -0.125 }}>:</Typography>
                            <TextField
                              select
                              size="small"
                              SelectProps={{ native: true }}
                              value={pickedMinute}
                              onChange={(e) => { setPickedMinute(e.target.value); if (typedTimeNote) setTypedTimeNote(null); }}
                              inputProps={{ 'aria-label': 'Minutes' }}
                            >
                              {MINUTE_CHOICES.map((m) => <option key={m} value={m}>{m}</option>)}
                            </TextField>
                            <TextField
                              select
                              size="small"
                              SelectProps={{ native: true }}
                              value={pickedPeriod}
                              onChange={(e) => { setPickedPeriod(e.target.value as 'AM' | 'PM'); if (typedTimeNote) setTypedTimeNote(null); }}
                              inputProps={{ 'aria-label': 'AM or PM' }}
                              className="ampm"
                            >
                              <option value="AM">AM</option>
                              <option value="PM">PM</option>
                            </TextField>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={handleAddTypedTime}
                              disabled={typedTimeBusy}
                              sx={{ textTransform: 'none', flex: '0 0 auto', minWidth: 0, px: 1.25, py: 0.25, fontSize: '0.8125rem' }}
                            >
                              {typedTimeBusy ? 'Checking…' : 'Add'}
                            </Button>
                          </Box>
                          {/* Which day it is for is never a guess — with Whole
                              week showing, the lists still mean the Date above. */}
                          <Typography
                            variant="caption"
                            color={typedTimeNote ? 'error' : 'text.secondary'}
                            role="status"
                            sx={{ display: 'block', mt: 0.5 }}
                          >
                            {typedTimeNote || `For ${slotDayHeading(slotOverrideDate, dateTimeFormat)} — change the Date above for another day.`}
                          </Typography>
                        </Collapse>
                      </Box>
                    )}

                    <Divider sx={{ my: 1.5 }} />

                    {/* A time the patient chose for themselves. It is held, not
                        booked — this is the only place in the app that can settle
                        it, which is why it sits at the top of the panel rather
                        than among the offer-times controls. */}
                    {tentativeBooking && (
                      <Box sx={{ mb: 2, p: 1.5, borderRadius: 1, bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.light' }}>
                        <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: 'warning.dark' }}>
                          The patient booked this themselves — it is held, not confirmed
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5, mb: 1 }}>
                          {practiceDateTimeLabel(tentativeBooking.startTime)}
                          {tentativeBooking.provider ? ` with ${tentativeBooking.provider}` : ''}
                        </Typography>
                        <FormControlLabel
                          sx={{ display: 'block', mb: 1 }}
                          control={
                            <Checkbox
                              size="small"
                              checked={tellPatientOnConfirm}
                              onChange={(e) => setTellPatientOnConfirm(e.target.checked)}
                            />
                          }
                          label={
                            <Typography variant="caption">
                              Also tell the patient — writes the message into the reply box. Nothing sends until you press Send.
                            </Typography>
                          }
                        />
                        <ActionHint title="Turns the held time into a real appointment on the calendar. The patient is only messaged if you tick the box above.">
                          <Button
                            variant="contained"
                            color="warning"
                            size="small"
                            onClick={handleConfirmBooking}
                            disabled={confirmingBooking}
                          >
                            {confirmingBooking ? 'Confirming…' : 'Confirm this booking'}
                          </Button>
                        </ActionHint>
                      </Box>
                    )}

                    {bookedAppointment && (
                      <Box sx={{ mt: 1, mb: 1, p: 1.25, borderRadius: 1, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.light' }}>
                        <Typography variant="caption" color="success.main" sx={{ display: 'block', fontWeight: 700 }}>
                          Booked — {bookedAppointment.label}
                        </Typography>
                        {bookedAppointment.earlier && (
                          <Typography variant="caption" color="warning.dark" sx={{ display: 'block', mb: 0.25 }}>
                            Their earlier appointment on {bookedAppointment.earlier} is still on the calendar — cancel it if this one replaces it.
                          </Typography>
                        )}
                        {stepsLeft && offerGroups.length > 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                            Still to book: {offerGroups.map((g) => g.title).join('; ')} — press Book on the time the patient agreed, below.
                          </Typography>
                        )}
                        {/* This said "has not been sent" as a fixed string, so it
                            went on saying it after the office had sent. The box
                            is the only place tying booking the slot to telling
                            the patient — it has to track both or it misleads. */}
                        {confirmationSentAt(bookedAppointment.bookedAt, (displayTask as any)?.conversationMessages) ? (
                          <Typography variant="caption" color="success.main" sx={{ display: 'block' }}>
                            It is on the calendar, and the patient was told at{' '}
                            {formatTimeOnly(
                              confirmationSentAt(bookedAppointment.bookedAt, (displayTask as any)?.conversationMessages) as Date,
                              dateTimeFormat,
                            )}.
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            It is on the calendar. The patient has not been told yet — the confirmation is in your reply box; press Send Response.
                          </Typography>
                        )}
                      </Box>
                    )}
                    {/* Book lives on the row, not on the block.
                        It used to appear only when exactly one time was chosen,
                        on the theory that one meant "the patient agreed this
                        one". That is not how the work goes: you offer two or
                        three, the patient replies naming one, and the times you
                        sent are still all here when you reopen the task — so the
                        moment you actually need to book is the moment the button
                        was hidden, and getting it back meant removing the others
                        first. Which time is the signal, and that is per row. */}

                    {/* The third way to work this task: hand the patient the
                        choice instead of making it for them. The office still
                        fixes the service and provider — the link only decides
                        WHEN — and what comes back is a request to confirm. */}
                    {selfBookingEnabled === true && (
                    <Box sx={{ mb: 1.5, p: 1.25, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
                      {!bookingLink ? (
                        <>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            {isRecallTask ? 'Wellness outreach' : 'Or let the patient pick their own time'}
                          </Typography>
                          {lapsedLink && (
                            <Typography variant="caption" color="warning.dark" role="status" sx={{ display: 'block', mb: 1 }}>
                              {lapsedLink} A new link would be a second one.
                            </Typography>
                          )}
                          {isRecallTask ? (
                          /* The recall's own path: the outreach wording from
                             Settings, with a link that books the lab visit
                             and lives a month rather than a week. Any times
                             chosen above are held and follow the wording. */
                          <ActionHint title={`Writes the wellness outreach message from Admin › Settings into your reply, with a link that books the ${selectedMapping?.service?.name || 'lab visit'} with ${selectedMapping?.specialist?.name || 'the nurse'}. ${aiSchedulingOverrideSlots.length ? `The ${aiSchedulingOverrideSlots.length === 1 ? 'time' : 'times'} you have chosen ${aiSchedulingOverrideSlots.length === 1 ? 'is' : 'are'} held for this patient and listed after it. ` : 'The patient sees every free time. '}Outreach links live longer than a reply — the length is set in Settings.`}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<LinkIcon fontSize="small" />}
                            disabled={bookingLinkBusy}
                            // Outreach goes to somebody who has not asked for
                            // anything, so letting them pick is the normal case
                            // here and holding times is the exception — the
                            // opposite of a reply to a patient who is waiting.
                            // Explicit either way: the tooltip above says which
                            // of the two this press will do.
                            onClick={() => handleCreateBookingLink({ purpose: 'wellness', openEnded: offerCount === 0 })}
                            sx={{ textTransform: 'none' }}
                          >
                            {bookingLinkBusy ? 'Drafting…' : 'Draft outreach message'}
                          </Button>
                          </ActionHint>
                          ) : (
                          <>
                          {holdImpact.message && (
                            <Typography
                              variant="caption"
                              role="status"
                              sx={{ display: 'block', mb: 1, fontWeight: 600, color: holdImpact.level === 'all' ? 'error.main' : 'warning.main' }}
                            >
                              {holdImpact.message}
                            </Typography>
                          )}
                          {/* Two actions, not one button whose meaning depends
                              on whether a list above happens to be populated.
                              Holding times and opening the diary are different
                              promises to a patient, and the second used to
                              happen by accident whenever the first was pressed
                              a moment too early. */}
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                            <ActionHint title={holdLinkHint(aiSchedulingOverrideSlots.map((item) => item.slot), selectedMapping, linkLifetimePhrase(linkHoldDays))}>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<LinkIcon fontSize="small" />}
                              disabled={bookingLinkBusy || slotOverrideLoading || offerCount === 0}
                              onClick={() => handleCreateBookingLink()}
                              sx={{ textTransform: 'none' }}
                            >
                              {bookingLinkBusy ? 'Creating…' : 'Create a booking link'}
                            </Button>
                            </ActionHint>
                            <ActionHint title={`Creates a link for this patient, this service and this provider, holding nothing. They see every free time and choose one; it arrives as a request for you to confirm, never as a confirmed appointment. Works once, expires ${linkLifetimePhrase(linkHoldDays)}.`}>
                            <Button
                              size="small"
                              variant="text"
                              disabled={bookingLinkBusy}
                              onClick={() => handleCreateBookingLink({ openEnded: true })}
                              sx={{ textTransform: 'none' }}
                            >
                              Let them pick any free time
                            </Button>
                            </ActionHint>
                          </Box>
                          {/* While times load the list above already says so;
                              two "finding" captions read as two things loading. */}
                          {offerCount === 0 && !slotOverrideLoading && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              Choose your times first — the link will hold them.
                            </Typography>
                          )}
                          </>
                          )}
                        </>
                      ) : (
                        <>
                          {/* What was promised, in the patient's own words —
                              the same formatters their page uses, so the two
                              sides cannot read differently. This used to be a
                              count at best, which answered none of the three
                              questions the office actually has: what did I
                              promise, is it really held, what can I do now. */}
                          <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: linkHeldSlots.length ? 'success.main' : 'warning.main', mb: 0.5 }}>
                            {linkHeldSlots.length
                              ? `${linkHeldSlots.length} ${linkHeldSlots.length === 1 ? 'time is' : 'times are'} held for this patient`
                              : 'An open link is out — nothing is held'}
                          </Typography>
                          {linkHeldSlots.length > 0 ? (
                            <Box component="ul" sx={{ m: 0, mb: 0.75, pl: 2.25 }}>
                              {linkHeldSlots.map((slot) => (
                                <Typography
                                  key={`${slot.date} ${slot.time} ${slot.providerName || ''}`}
                                  component="li"
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: 'list-item' }}
                                >
                                  {patientDayLabel(slot.date)} — {patientTimeLabel(slot.time)}
                                  {/* Which appointment, when the link carries more
                                      than one: a lab draw and a doctor's visit
                                      are held in two different diaries. */}
                                  {linkHoldsSeveral && (slot.serviceName || slot.providerName)
                                    ? ` · ${[slot.serviceName, slot.providerName].filter(Boolean).join(' with ')}`
                                    : ''}
                                </Typography>
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                              The patient can pick any free time in the diary. Nothing is reserved for them, so a time you offered can still be taken by somebody else.
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                            {linkHeldSlots.length ? 'Nobody else is offered those times while it is live. ' : ''}
                            {/* The same deadline the patient is told, to the
                                minute — "expires 10/3/2026 (1 days)" said
                                less and said it badly. */}
                            Works once, until {heldUntilLabel(bookingLink.expiresAt, getPracticeTimeZone()) || new Date(bookingLink.expiresAt).toLocaleDateString()}.
                            {' '}
                            {/* Reopening a task clears the reply box, so the old
                                fixed string told staff to press Send Response on
                                an empty message while the link sat nowhere. The
                                wording is now a function of what is true. */}
                            {describeLinkSend({
                              sentAtLabel: linkSentWhen ? formatTimeOnly(linkSentWhen, dateTimeFormat) : null,
                              urlInReply: Boolean(bookingLink.url) && response.includes(bookingLink.url),
                            })}
                          </Typography>
                          {linkDrift.drifted && (
                            <Typography variant="caption" sx={{ display: 'block', mb: 0.75, color: 'warning.main', fontWeight: 600 }}>
                              {linkDrift.message}
                            </Typography>
                          )}
                          {!bookingLink.url && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                              This link was made before links could be shown again, so it cannot be copied. Send a new one if the patient needs it.
                            </Typography>
                          )}
                          {/* Kept for pasting somewhere else entirely — Spruce
                              directly, or an email. */}
                          {bookingLink.url && (
                          <TextField
                            value={bookingLink.url}
                            size="small"
                            fullWidth
                            inputRef={bookingLinkFieldRef}
                            InputProps={{ readOnly: true }}
                            onFocus={(e) => e.target.select()}
                            sx={{ mb: 0.75, '& input': { fontSize: '0.75rem' } }}
                          />
                          )}
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {bookingLink.url && (<>
                            <ActionHint title="Copies the link on its own, for pasting somewhere other than your reply.">
                            <Button
                              size="small"
                              variant={linkCopied ? 'contained' : 'outlined'}
                              color={linkCopied ? 'success' : 'primary'}
                              startIcon={linkCopied ? <CheckCircleIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
                              onClick={() => copyLinkToClipboard(bookingLink.url)}
                              sx={{ textTransform: 'none' }}
                            >
                              {linkCopied ? 'Copied' : 'Copy link'}
                            </Button>
                            </ActionHint>
                            <ActionHint title="Puts the invitation back into your reply if you have deleted it. It is added to whatever is there, never over it.">
                            <Button
                              size="small"
                              variant="text"
                              onClick={handleAddLinkToReply}
                              sx={{ textTransform: 'none' }}
                            >
                              Add to reply
                            </Button>
                            </ActionHint>
                            </>)}
                            {/* The way out of every wrong state: the offer
                                drifted, the link holds nothing, or it cannot
                                be shown again. One press withdraws what is out
                                and mints a link holding what the list says now. */}
                            {(linkDrift.drifted || linkHeldSlots.length === 0 || !bookingLink.url) && offerCount > 0 && (
                            <ActionHint title={`Withdraws the link that is out and creates a new one holding the ${offerCount === 1 ? 'time' : `${offerCount} times`} you have chosen. The old link stops working immediately, so only do this if the patient has not already taken a time.`}>
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={bookingLinkBusy}
                              onClick={() => handleReplaceBookingLink()}
                              sx={{ textTransform: 'none' }}
                            >
                              {linkHeldSlots.length === 0 ? 'Hold the chosen times instead' : 'Send a new link with the chosen times'}
                            </Button>
                            </ActionHint>
                            )}
                            <ActionHint title={linkHeldSlots.length
                              ? 'Withdraws this link and releases the times it holds. Anyone who already has it will find it no longer works.'
                              : 'Withdraws this link. Anyone who already has it will find it no longer works.'}>
                            <Button size="small" variant="text" color="error" disabled={bookingLinkBusy}
                              onClick={handleRevokeBookingLink} sx={{ textTransform: 'none' }}>
                              Withdraw
                            </Button>
                            </ActionHint>
                          </Box>
                        </>
                      )}
                    </Box>
                    )}

                    {aiSchedulingOverrideSlots.length === 0 ? (
                      <>
                        <Typography variant="caption" color="text.secondary">
                          Times you're offering
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          None chosen yet — tap the times above.
                        </Typography>
                      </>
                    ) : (
                      <Box
                        sx={{
                          mt: 0.5,
                          p: 1.5,
                          borderRadius: 1,
                          bgcolor: 'primary.50',
                          border: '1px solid',
                          borderColor: 'primary.light',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                        }}
                      >
                        {/* One kind of appointment: say who with once, here, instead
                            of on every chip — on a phone the name was what got cut. */}
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.dark' }}>
                          {aiSchedulingOverrideSlots.length === 1
                            ? 'One time chosen'
                            : `${aiSchedulingOverrideSlots.length} times chosen`}
                          {offerGroups.length === 1 && offerGroups[0].title ? ` — ${offerGroups[0].title}` : ''}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Send them as options, or book the one the patient has agreed.
                        </Typography>
                        {/* Two kinds of appointment in one offer — the set
                            buttons' lab draw and doctor's visit — are shown as
                            numbered steps, each with its own "add a time". One
                            kind is the plain list it always was. */}
                        {(offerGroups.length > 1 ? offerGroups : [null]).map((group, groupIndex) => (
                          <React.Fragment key={group ? `${group.serviceId}|${group.specialistId}` : 'all'}>
                            {group && (
                              <Typography variant="caption" sx={{ fontWeight: 700, mt: groupIndex ? 0.5 : 0 }}>
                                {groupIndex + 1}. {group.title}
                              </Typography>
                            )}
                            {(group ? group.items : aiSchedulingOverrideSlots).map((item) => (
                          // minWidth 0 on both the row and the chip: without it
                          // the chip refuses to shrink and pushes Remove outside
                          // the block's right edge on a phone.
                          <Box key={item.key} sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                            <Chip
                              size="small"
                              label={formatSlotLabel({ ...item.slot, specialist: '' }, dateTimeFormat)}
                              // On a phone the date was cut to "10/07/20…" by the two
                              // buttons beside it. There the time takes its own line
                              // and the buttons sit beneath; wider, one row as before.
                              sx={{ minWidth: 0, flex: { xs: '1 1 100%', sm: '1 1 auto' }, justifyContent: 'flex-start', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
                            />
                            <ActionHint title="Puts this appointment on the calendar now and writes a confirmation into your reply. Nothing reaches the patient until you press Send Response. Use it once they have agreed this time.">
                            <Button
                              size="small"
                              variant="contained"
                              disabled={bookingSlotKey !== null}
                              onClick={() => handleBookSlot(item.key)}
                              sx={{ flexShrink: 0, textTransform: 'none' }}
                            >
                              {bookingSlotKey === item.key ? 'Booking…' : 'Book'}
                            </Button>
                            </ActionHint>
                            <ActionHint title="Takes this time out of the list. It stays available on the calendar.">
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => handleRemoveOverrideSlot(item.key)}
                              sx={{ flexShrink: 0, textTransform: 'none' }}
                            >
                              Remove
                            </Button>
                            </ActionHint>
                          </Box>
                            ))}
                            {group && (
                              <ActionHint title="Points the picker above at this step's service, provider and day, so the times listed there and a time that is not listed add to this step.">
                              <Button size="small" variant="text" onClick={() => handleAddTimeForStep(group)}
                                sx={{ alignSelf: 'flex-start', textTransform: 'none', py: 0, minHeight: 0 }}>
                                Add a time for this step
                              </Button>
                              </ActionHint>
                            )}
                          </React.Fragment>
                        ))}
                        {offerWarnings.map((warning) => (
                          <Typography key={warning} variant="caption" sx={{ color: 'warning.dark' }}>
                            {warning}
                          </Typography>
                        ))}

                        {aiSchedulingOverrideSlots.length > 1 && (
                          <Typography variant="caption" color="text.secondary">
                            Booking one of these puts it on the calendar and stops the others being offered.
                          </Typography>
                        )}

                        {/* Only shown when it has something to do. Adding times
                            already writes them into the message, so in the normal
                            path this button changed nothing and read as broken. */}
                        {draftMissingSlots && (
                          <Box>
                            <Typography variant="caption" color="warning.main" sx={{ display: 'block', mb: 0.75 }}>
                              These times aren't in your reply yet.
                            </Typography>
                            <ActionHint title="Writes these times into your reply as a numbered list. It replaces what is in the box, so copy anything of your own you want to keep.">
                            <Button size="small" variant="contained" onClick={handleUseOverrideSlots} sx={{ textTransform: 'none' }}>
                              Put these times in your reply
                            </Button>
                            </ActionHint>
                          </Box>
                        )}

                        <Box>
                          <ActionHint title="Empties this list. Anything already booked stays booked.">
                          <Button size="small" variant="text" onClick={handleClearOverrideSlots} sx={{ textTransform: 'none' }}>
                            Start the list again
                          </Button>
                          </ActionHint>
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}
                {aiSchedulingError && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    {aiSchedulingError}
                  </Alert>
                )}
              </Paper>
            )}


          </Grid>
        </Grid>
      </DialogContent>

      {/* Where the task stands — always on screen. The team asked for these
          below the communication window; directly under the reply box they
          were below the fold on a laptop (Complete at y=949 of 714 shown),
          and on a phone they scrolled away with the left column. Outside the
          scrolling area they are below everything, at every size, without
          scrolling. Compact and to the right on a desktop; sharing the width
          on a phone, clear of the home indicator. */}
      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 },
          pt: 1.25,
          pb: 'calc(10px + env(safe-area-inset-bottom, 0px))',
          gap: 1,
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          '& > :not(style) ~ :not(style)': { ml: 0 },
        }}
      >
        {/* One control, three states, the current one marked. Any of them is
            one tap away — including back to To do, and reopening a completed
            task, which the two forward-only buttons never allowed. */}
        <ToggleButtonGroup
          exclusive
          size="small"
          value={statusChoices(task.status).find((c) => c.current)?.key}
          onChange={(_, next: TaskStatusKey | null) => { if (next) handleSetStatus(next); }}
          disabled={isLoading}
          aria-label="Task status"
          sx={{
            flex: { xs: 1, sm: '0 0 auto' },
            '& .MuiToggleButton-root': {
              textTransform: 'none', whiteSpace: 'nowrap', px: { xs: 1, sm: 2 }, flex: { xs: 1, sm: '0 0 auto' },
              fontWeight: 600,
            },
            '& .MuiToggleButton-root.Mui-selected': { fontWeight: 700 },
          }}
        >
          {statusChoices(task.status).map((choice) => (
            <ToggleButton
              key={choice.key}
              value={choice.key}
              color={choice.key === 'completed' ? 'success' : choice.key === 'inprogress' ? 'warning' : 'standard'}
              title={choice.hint}
              aria-label={choice.label}
            >
              {choice.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        {isLoading && <CircularProgress size={18} sx={{ flex: '0 0 auto' }} />}
        <Tooltip title="Assign, snooze, or open the patient's chart." placement="top" arrow enterDelay={400}>
          <IconButton
            aria-label="More task actions"
            onClick={(e) => setTaskActionsAnchor(e.currentTarget)}
            sx={{ border: 1, borderColor: 'divider', borderRadius: 1, flex: '0 0 auto' }}
          >
            <MoreVertIcon />
          </IconButton>
        </Tooltip>
      </DialogActions>

      {/* Appointment Modal - draggable so it can be moved while viewing task details */}
      <AppointmentModal
        open={showAppointmentModal}
        appointment={null}
        onClose={() => setShowAppointmentModal(false)}
        onSave={handleAppointmentSave}
        initialPatient={{
          name: displayTask.patientName,
          phone: task.patientPhone,
          email: task.patientEmail,
          internalId: task.internalPatientId,
        }}
        draggable={true}
      />

      {/* Link/relink the task to an existing patient (identity only). */}
      <PatientLinkPicker
        open={linkPickerOpen}
        onClose={() => setLinkPickerOpen(false)}
        onSelect={handleRelinkPatient}
        busy={linkBusy}
        error={linkError}
      />
    </Dialog>

    <Dialog
      open={assignDialogOpen}
      onClose={() => setAssignDialogOpen(false)}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>Assign Task</DialogTitle>
      <DialogContent>
        {assignLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <TextField
            select
            fullWidth
            label="Assign to"
            value={selectedAssigneeId}
            onChange={(e) => setSelectedAssigneeId(e.target.value)}
            sx={{ mt: 1 }}
          >
            {assignees.map((user) => (
              <MenuItem key={user.id} value={user.id}>
                {user.name || user.email}
              </MenuItem>
            ))}
          </TextField>
        )}
        {assignError && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
            {assignError}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleAssignSave}
          disabled={assignLoading || !selectedAssigneeId}
        >
          Assign
        </Button>
      </DialogActions>
    </Dialog>

    <Dialog
      open={followUpDialogOpen}
      onClose={() => setFollowUpDialogOpen(false)}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>Schedule Follow Up</DialogTitle>
      <DialogContent>
        <TextField
          type="date"
          fullWidth
          label="Follow-up date"
          value={followUpDate}
          onChange={(e) => setFollowUpDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: formatDateInputValue(new Date()) }}
          sx={{ mt: 1 }}
        />
        {followUpError && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
            {followUpError}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setFollowUpDialogOpen(false)}>Cancel</Button>
        <Button variant="contained" onClick={handleFollowUpSave} disabled={followUpLoading}>
          {followUpLoading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>

    <Dialog
      open={mergeDialogOpen}
      onClose={handleCloseMergeDialog}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Merge duplicate contacts</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary">
          We found potential duplicates for this patient. Select the contacts to merge into the updated record.
        </Typography>
        <List sx={{ mt: 2, maxHeight: 240, overflow: 'auto' }}>
          {mergeCandidates.map((candidate) => {
            const isChecked = mergeSelection.includes(candidate.id);
            return (
              <ListItem key={candidate.id} disablePadding>
                <ListItemButton onClick={() => handleToggleMergeSelection(candidate.id)}>
                  <Checkbox edge="start" checked={isChecked} tabIndex={-1} disableRipple />
                  <ListItemText
                    primary={candidate.fullName || 'Unknown'}
                    secondary={`${candidate.phone || 'No phone'}${candidate.isArchived ? ' • archived' : ''}`}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
        {mergeError && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
            {mergeError}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseMergeDialog}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleMergeContacts}
          disabled={mergeLoading || mergeSelection.length === 0}
        >
          {mergeLoading ? 'Merging...' : 'Merge Selected'}
        </Button>
      </DialogActions>
    </Dialog>
    <Snackbar
      open={Boolean(labAttachToast)}
      autoHideDuration={4000}
      onClose={(_, reason) => {
        if (reason === 'clickaway') return;
        setLabAttachToast(null);
      }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert
        onClose={() => setLabAttachToast(null)}
        severity="success"
        sx={{ width: '100%' }}
      >
        {labAttachToast}
      </Alert>
    </Snackbar>
    <Snackbar
      open={Boolean(patientToast)}
      autoHideDuration={4000}
      onClose={(_, reason) => {
        if (reason === 'clickaway') return;
        setPatientToast(null);
      }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert
        onClose={() => setPatientToast(null)}
        severity="success"
        sx={{ width: '100%' }}
      >
        {patientToast}
      </Alert>
    </Snackbar>
    <Dialog
      open={Boolean(attachmentPreview)}
      onClose={handleClosePreview}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        {attachmentPreview?.filename || 'Attachment'}
      </DialogTitle>
      <DialogContent sx={{ minHeight: 200 }}>
        {isPreviewLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {!isPreviewLoading && attachmentPreview && (
          <>
            {attachmentPreviewText ? (
              <Box
                component="pre"
                sx={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontSize: '0.9rem',
                  bgcolor: 'grey.50',
                  p: 2,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'grey.200'
                }}
              >
                {attachmentPreviewText}
              </Box>
            ) : attachmentPreview.contentType?.startsWith('image/') ? (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Box
                  component="img"
                  src={attachmentPreview.url}
                  alt={attachmentPreview.filename}
                  sx={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 1 }}
                />
              </Box>
            ) : attachmentPreview.contentType === 'application/pdf' ? (
              <Box
                component="iframe"
                src={attachmentPreview.url}
                sx={{ width: '100%', height: '70vh', border: 'none' }}
              />
            ) : attachmentPreview.contentType?.startsWith('audio/') ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <audio controls src={attachmentPreview.url} style={{ width: '100%' }} />
                {attachmentPreview.transcription && (
                  <Typography variant="body2" color="text.secondary">
                    Transcription: {attachmentPreview.transcription}
                  </Typography>
                )}
              </Box>
            ) : (
              <Typography variant="caption" color="text.secondary">
                Preview unavailable. Click to view/download.
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {attachmentPreview?.url && (
          <Button
            onClick={() => window.open(attachmentPreview.url, '_blank', 'noopener')}
          >
            Open in New Tab
          </Button>
        )}
        {attachmentPreviewSource && (
          <Button
            onClick={() => handleDownloadAttachment(attachmentPreviewSource.attachment, attachmentPreviewSource.message)}
          >
            Download
          </Button>
        )}
        <Button onClick={handleClosePreview}>
          Close
        </Button>
      </DialogActions>
    </Dialog>

    <ConfirmDialog
      open={confirmDeleteTask}
      title="Delete this task?"
      body={
        <>
          <DialogContentText>
            This removes the wellness reminder the system created. Nothing the patient sent is
            affected — a recall task carries no patient message.
          </DialogContentText>
          <DialogContentText sx={{ mt: 1.5 }}>
            {displayTask?.patientName || 'The patient'} stays on the recall list and can be offered
            outreach again.
          </DialogContentText>
        </>
      }
      confirmLabel="Delete task"
      busy={deletingTask}
      onConfirm={deleteThisTask}
      onClose={() => setConfirmDeleteTask(false)}
    />

    <ConfirmDialog
      open={Boolean(noteToDelete)}
      title="Delete this note?"
      body={
        <>
          <DialogContentText>This cannot be undone.</DialogContentText>
          {noteToDelete?.text && (
            <DialogContentText
              sx={{
                mt: 1.5,
                p: 1.25,
                borderLeft: '3px solid',
                borderColor: 'divider',
                fontStyle: 'italic',
                whiteSpace: 'pre-wrap',
                overflowWrap: 'anywhere',
              }}
            >
              {noteToDelete.text.length > 240
                ? `${noteToDelete.text.slice(0, 240)}…`
                : noteToDelete.text}
            </DialogContentText>
          )}
        </>
      }
      confirmLabel="Delete note"
      busy={Boolean(noteToDelete && noteBusyId === noteToDelete.id)}
      onConfirm={confirmDeleteNote}
      onClose={() => setNoteToDelete(null)}
    />
    </>
  );
};

export default TaskDetailsModal;

